/**
 * Sticker Quote Calculator — vanilla JS widget
 * Reads all config from data-* attributes on #sticker-quote-app.
 * No build step required.
 */
(function () {
  'use strict';

  var IN_TO_MM       = 25.4;
  var MAX_BYTES      = 10 * 1024 * 1024;
  var MIN_PIXELS     = 12000000;
  var PANE_W         = 900;  // designer canvas backing store (4:3); CSS scales it to its column
  var PANE_H         = 675;
  var DEFAULT_PRESET = 1;    // second tier (75 x 50 mm)

  // PDF is not accepted: it can't be placed in the designer preview.
  var ACCEPTED_EXTS  = { svg: true, jpg: true, jpeg: true, png: true };
  var ACCEPTED_MIME  = { 'image/png': true, 'image/jpeg': true, 'image/svg+xml': true };
  var BITMAP_MIME    = { 'image/png': true, 'image/jpeg': true };
  var SHAPE_NAMES    = { rect: 'Rectangular', square: 'Square', circle: 'Circular', diecut: 'Die-cut' };
  var MATERIAL_NAMES = { vinyl: 'Vinyl', holographic: 'Holographic', clear: 'Clear', paper: 'Paper' };
  var MATERIAL_NOTES = {
    vinyl:       'Glossy white vinyl.',
    holographic: 'Holographic film. The rainbow sheen in the preview is only a hint.',
    clear:       'Clear film. The checkerboard shows where nothing is printed — unprinted areas stay see-through.',
    paper:       'Matte white paper.'
  };
  var DEFAULT_BORDER_COLOR = '#111111';
  // Holographic and clear stickers can't have white printed on them (the printers have no white ink).
  function materialNoWhite(m) { return m === 'holographic' || m === 'clear'; }
  function hexNearWhite(hex) {
    var m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
    if (!m) return false;
    var n = parseInt(m[1], 16);
    return Math.min((n >> 16) & 255, (n >> 8) & 255, n & 255) >= 215;
  }
  var DEFAULT_PAD_MM       = 1.5;
  var RMBG_DEFAULT_TOL     = 40;

  var ORDER_STORAGE_KEY = 'sqc_order_runs';
  var DRAFT_ORDER_KEY   = 'sqc_draft_order';
  var DAY_MS            = 24 * 60 * 60 * 1000;

  // How long the worker keeps artwork, from GET /config. Null until that
  // answers, and it stays null if the call fails — in which case the copy stays
  // hidden and nothing is marked expired, rather than guessing a number.
  var retentionDays = null;

  function fetchRetention(workerUrl) {
    if (!workerUrl) return Promise.resolve(null);
    return fetch(workerUrl + '/config')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        var n = d && Number(d.retentionDays);
        retentionDays = isFinite(n) && n > 0 ? n : null;
        return retentionDays;
      })
      .catch(function () { return null; });
  }

  // A run whose artwork the worker may already have swept. Snapshots saved
  // before this shipped carry no uploadedAt and predate the manifests that
  // would have protected their files, so they count as expired rather than
  // being trusted. With no retention period known, nothing is blocked.
  function runExpired(run) {
    if (retentionDays == null) return false;
    if (!run.uploadedAt) return true;
    var t = Date.parse(run.uploadedAt);
    return !isFinite(t) || Date.now() - t > retentionDays * DAY_MS;
  }

  function getExt(filename) {
    return (filename.split('.').pop() || '').toLowerCase();
  }

  // ─── Config ──────────────────────────────────────────────────────────────────

  var BLEED_MIN_MM = 2, BLEED_DEFAULT_MM = 3, BLEED_MAX_MM = 5;

  // Slider range from data-*-min/max/default; blank or bad values fall back.
  function readRange(el, name, def, fbMax, fbMin) {
    function n(v, fb) { var x = parseFloat(v); return isFinite(x) ? x : fb; }
    var min = n(el.dataset[name + 'Min'], fbMin != null ? fbMin : 0);
    var max = n(el.dataset[name + 'Max'], fbMax != null ? fbMax : 100);
    if (max < min) max = min;
    return { min: min, max: max, value: Math.min(max, Math.max(min, n(el.dataset[name + 'Default'], def))) };
  }

  // Shown when the section setting is blank, e.g. a section saved before the
  // setting existed. Keep in step with the calculator-home schema default.
  var DEFAULT_APPROVAL_TEXT = 'I confirm that I have checked my design, including all spelling, artwork quality, dimensions, colours and placement. I understand that Stroop Studio will print my artwork as submitted without providing a separate proof, and that customer design errors cannot normally be refunded or returned. My statutory rights remain unaffected.';

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function readConfig(el) {
    var tiers = [];
    try { tiers = JSON.parse(el.dataset.discountTiers || '[]'); } catch (_) {}
    tiers.sort(function (a, b) { return a.min_qty - b.min_qty; });
    return {
      corner:             readRange(el, 'corner', 3),
      margin:             readRange(el, 'margin', 2),
      bleed:              (function () { // never below the print floor: anything printed out to the cut line needs 2-3 mm of bleed
        var r = readRange(el, 'bleed', BLEED_DEFAULT_MM, BLEED_MAX_MM, BLEED_MIN_MM);
        if (r.min < BLEED_MIN_MM) r.min = BLEED_MIN_MM;
        if (r.max < r.min) r.max = r.min;
        r.value = Math.min(r.max, Math.max(r.min, r.value));
        return r;
      })(),
      pad:                readRange(el, 'pad', DEFAULT_PAD_MM, 10),
      // % added to the per-sticker part of the price for each material (vinyl is the base)
      materialPct:        {
        vinyl: 0,
        holographic: (function (v) { return isFinite(v) ? v : 0; })(parseFloat(el.dataset.materialHolographicPct)),
        clear:       (function (v) { return isFinite(v) ? v : 0; })(parseFloat(el.dataset.materialClearPct)),
        paper:       (function (v) { return isFinite(v) ? v : 0; })(parseFloat(el.dataset.materialPaperPct))
      },
      fontsUrl:           el.dataset.epsFontsUrl || '',
      productId:          el.dataset.productId  || '',
      variantId:          el.dataset.variantId  || '',
      minQty:             parseInt(el.dataset.minQty, 10)            || 1,
      maxWidth:           parseInt(el.dataset.maxWidth, 10)          || 300,
      maxHeight:          parseInt(el.dataset.maxHeight, 10)         || 300,
      basePrice:          parseFloat(el.dataset.basePrice)           || 0.035,
      orderFee:           (function (v) { v = parseFloat(v); return isNaN(v) ? 0 : v; })(el.dataset.orderFee),
      perSticker:         (function (v) { v = parseFloat(v); return isNaN(v) ? 0 : v; })(el.dataset.perSticker),
      turnaround:         parseInt(el.dataset.turnaround, 10)        || 7,
      expeditedReduction: parseInt(el.dataset.expeditedReduction, 10)|| 3,
      expeditedSurcharge: parseFloat(el.dataset.expeditedSurcharge)  || 20,
      tiers:              tiers,
      currency:           el.dataset.currency       || 'GBP',
      cartUrl:            el.dataset.routesCartAdd  || '/cart/add.js',
      workerUrl:          (function (raw) {
        var u = (raw || '').trim().replace(/\/+$/, '');
        if (!u) return '';
        if (!/^https?:\/\//i.test(u)) u = 'https://' + u;
        try { new URL(u); } catch (_) { return ''; }
        return u;
      })(el.dataset.uploadWorkerUrl),
      contactUrl:         el.dataset.contactUrl     || '/pages/contact',
      artworkApproval:    (el.dataset.artworkApproval || '').trim() || DEFAULT_APPROVAL_TEXT,
      customerEmail:      (window.sqcCustomer && window.sqcCustomer.email)  || '',
      customerId:         (window.sqcCustomer && window.sqcCustomer.id)     || null,
    };
  }

  // ─── Pricing ─────────────────────────────────────────────────────────────────

  function getDiscount(qty, tiers) {
    for (var i = tiers.length - 1; i >= 0; i--) {
      var t = tiers[i];
      if (qty >= t.min_qty && (t.max_qty === null || qty <= t.max_qty)) return t.discount_pct || 0;
    }
    return 0;
  }

  // Price = order fee + (per-sticker price + rate per 1,000 mm²) x quantity.
  // Bulk discounts apply to the per-sticker part only; the order fee covers postage and packing.
  // Die-cut is priced like a rectangle of its real outline size (see pricedDims).
  // The material adjustment applies to the per-sticker part, before bulk discounts.
  function calcQuote(wMm, hMm, qty, cfg, expedited, shape, material) {
    if (wMm <= 0 || hMm <= 0 || qty <= 0) return null;
    var matPct = (cfg.materialPct && cfg.materialPct[material || 'vinyl']) || 0;
    var area = shape === 'circle'
      ? Math.PI * Math.pow(wMm / 2, 2) / 1000
      : (wMm * hMm) / 1000;
    var discount  = getDiscount(qty, cfg.tiers);
    var perUnit   = (cfg.perSticker + cfg.basePrice * area) * (1 + matPct / 100) * (1 - discount / 100);
    var total     = cfg.orderFee + perUnit * qty;
    if (expedited) total *= (1 + cfg.expeditedSurcharge / 100);
    total = Math.round(total * 100) / 100;
    var days = cfg.turnaround - (expedited ? cfg.expeditedReduction : 0);
    return { unitPrice: total / qty, totalPrice: total, discount: discount, days: days, materialPct: matPct };
  }

  // What a run is priced on. Die-cut is priced on the real size of its cut outline (the size the preview
  // shows, border and padding included), rounded up to a whole mm; everything else on the chosen size.
  function pricedDims(state) {
    if (state.shape === 'diecut' && state.cutMm && state.cutMm.w > 0 && state.cutMm.h > 0) {
      return { w: Math.max(1, Math.ceil(state.cutMm.w - 0.05)), h: Math.max(1, Math.ceil(state.cutMm.h - 0.05)) };
    }
    return { w: state.widthMm, h: state.heightMm };
  }
  // Does anything print out to the cut line, so the sticker needs bleed? (a fill colour behind the
  // logo, a printed die-cut padding colour or none at all, or full-fill artwork that runs out past the cut line)
  function bleedApplies(state) {
    // die-cut: a printed padding colour, or no padding at all (the cut sits on the artwork edge, so its edge colours are bled outward)
    if (state.shape === 'diecut') return !!state.padColor || (state.padMm === 0 && (state.mode === 'text' || !!state.image));
    if (state.mode !== 'logo' || !state.image) return false;
    return !!state.bgEnabled || !!state.fullFill;
  }
  function quoteFor(state, cfg) {
    var d = pricedDims(state);
    return calcQuote(d.w, d.h, state.qty, cfg, state.expedited, state.shape, state.material);
  }

  function fmtCurrency(amount, currency) {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency, minimumFractionDigits: 2 }).format(amount);
  }

  // ─── Styles ──────────────────────────────────────────────────────────────────

  var CSS = [
    '.sqc [hidden]{display:none!important}',
    '.sqc{max-width:1040px;margin:0 auto;padding:2rem 0;font-family:inherit}',
    '.sqc__title{font-family:var(--font-heading,inherit);font-size:1.5rem;margin:0 0 1.5rem}',
    '.sqc__field{margin-bottom:1.5rem}',
    '.sqc__label,.sqc__fieldset legend{display:block;font-weight:600;margin-bottom:.5rem;font-size:.9rem}',
    '.sqc__fieldset{border:none;padding:0;margin:0}',
    '.sqc__req{color:var(--color-accent,#e44)}',
    '.sqc__hint{font-size:.8rem;opacity:.6;margin:.25rem 0 0}',

    '.sqc__dropzone{position:relative;border:2px dashed currentColor;border-radius:6px;padding:2rem;text-align:center;cursor:pointer;opacity:.7;transition:opacity .2s,background .2s}',
    '.sqc__dropzone:hover,.sqc__dropzone--drag{opacity:1;background:rgba(0,0,0,.04)}',
    '.sqc__dropzone-content{pointer-events:none}',
    '.sqc__dropzone-primary{margin:.5rem 0 .25rem;font-weight:600}',
    '.sqc__dropzone-secondary{margin:0;font-size:.9rem}',
    '.sqc__browse-link{text-decoration:underline;color:var(--color-accent,#e44)}',
    '.sqc__dropzone-hint{font-size:.8rem;opacity:.6;margin:.5rem 0 0}',
    '.sqc__file-input{position:absolute;inset:0;opacity:0;cursor:pointer;width:100%;height:100%}',

    '.sqc__file-preview{display:flex;align-items:center;gap:.5rem;padding:.5rem .75rem;border-radius:4px;background:rgba(0,0,0,.05);margin-top:.5rem;font-size:.875rem}',
    '.sqc__file-name{flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}',
    '.sqc__file-remove{background:none;border:none;cursor:pointer;font-size:1.25rem;line-height:1;padding:0;opacity:.6;flex-shrink:0}',
    '.sqc__file-remove:hover{opacity:1}',

    '.sqc__res-warning{display:flex;align-items:flex-start;gap:.5rem;padding:.6rem .875rem;margin-top:.5rem;background:#fff8e1;border:1px solid #ffe082;border-radius:4px;font-size:.85rem;color:#5d4037}',
    '.sqc__res-warning span{flex:1}',
    '.sqc__res-dismiss{background:none;border:none;cursor:pointer;font-size:1.1rem;line-height:1;padding:0;opacity:.6;flex-shrink:0;margin-left:auto}',
    '.sqc__res-dismiss:hover{opacity:1}',

    // Design pane (StroopEpsDesigner canvas)
    '.sqc__design-pane{margin-bottom:1.5rem}',
    '.sqc__pane-label{font-weight:600;font-size:.9rem;margin:0 0 .5rem}',
    '.sqc__pane-canvas{display:block;width:100%;height:auto;aspect-ratio:' + PANE_W + '/' + PANE_H + ';background:#fff;border:1px solid rgba(0,0,0,.14);border-radius:6px}',
    // the preview window: size tools on the left, the canvas on the right (stacks when narrow)
    '.sqc__stage-wrap{container-type:inline-size}',
    '.sqc__stage{display:flex;flex-wrap:wrap;gap:.875rem;align-items:flex-start;border:1px solid rgba(0,0,0,.12);border-radius:8px;padding:.75rem;background:rgba(0,0,0,.025)}',
    '.sqc__stage-tools{flex:0 0 310px;min-width:0}',
    '.sqc__stage-tools .sqc__row{margin-bottom:.5rem;background:#fff}',
    '.sqc__stage-tools .sqc__row:last-child{margin-bottom:0}',
    '.sqc__stage-tools .sqc__size-row{flex-direction:column;align-items:stretch;gap:.5rem}',
    '.sqc__stage-tools .sqc__radio-group{flex-direction:column;flex-wrap:nowrap;gap:.35rem}',
    '.sqc__stage-tools .sqc__radio-label{font-size:.8rem;padding:.4rem .6rem}',
    '.sqc__stage-tools .sqc__unit-toggle{align-self:flex-start}',
    '.sqc__stage-tools .sqc__dim-input{min-width:0;flex:1 1 0}',
    '.sqc__stage-tools .sqc__dimensions{flex-wrap:nowrap;gap:.35rem}',
    '.sqc__stage-tools .sqc__input{padding:.45rem .5rem;font-size:.9rem}',
    '.sqc__stage-view{position:relative;flex:1 1 380px;min-width:0}',
    '.sqc__empty{position:absolute;left:50%;bottom:9%;transform:translateX(-50%);margin:0;padding:.3rem .75rem;border-radius:999px;background:rgba(255,255,255,.85);font-size:.8rem;font-weight:600;white-space:nowrap;pointer-events:none;box-shadow:0 1px 3px rgba(0,0,0,.15)}',
    '.sqc__pane-help{font-size:.8rem;opacity:.65;margin:.5rem 0 0}',
    '.sqc__pane-status{font-size:.85rem;margin:.375rem 0 0}',
    '.sqc__pane-status--warn{color:#b45309}',
    '.sqc__pane-status--error{color:#c00}',
    '.sqc__pane-btn{padding:.55rem 1rem;border:none;border-radius:6px;font-size:.875rem;font-weight:600;cursor:pointer;font-family:inherit;transition:opacity .2s}',
    '.sqc__pane-btn--secondary{background:rgba(0,0,0,.08);color:inherit}',
    '.sqc__pane-btn--secondary:hover{background:rgba(0,0,0,.14)}',
    '.sqc__pane-btn--active{background:var(--color-accent,#e44);color:#fff}',
    '.sqc__pane-btn--sm{padding:.3rem .75rem;flex-shrink:0}',
    '.sqc__pane-btn:disabled{opacity:.5;cursor:not-allowed}',
    '.sqc__subbox{border:1px solid rgba(0,0,0,.12);border-radius:6px;padding:.875rem 1rem;margin-top:.75rem}',
    '.sqc__edit-note{font-size:.85rem;padding:.6rem .875rem;margin-top:.5rem;background:rgba(0,0,0,.05);border-radius:4px}',

    // Swatches, checkboxes, text controls, sliders
    '.sqc__swatches{display:flex;flex-wrap:wrap;gap:.4rem;margin-top:.5rem}',
    '.sqc__swatch{width:28px;height:28px;border-radius:50%;border:2px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,.25);cursor:pointer;padding:0}',
    '.sqc__swatch--selected{box-shadow:0 0 0 2px var(--color-accent,#e44)}',
    '.sqc__current-colour{display:flex;align-items:center;gap:.5rem;font-size:.85rem;margin-top:.625rem}',
    '.sqc__current-swatch{width:18px;height:18px;border-radius:4px;box-shadow:0 0 0 1px rgba(0,0,0,.25);flex-shrink:0}',
    '.sqc__checks{display:flex;flex-wrap:wrap;gap:1.25rem;margin-top:.625rem}',
    '.sqc__check{display:flex;align-items:center;gap:.5rem;font-size:.9rem;cursor:pointer}',
    '.sqc__check input{width:18px;height:18px;margin:0;accent-color:var(--color-accent,#e44)}',
    '.sqc__textarea{min-height:5rem;resize:vertical}',
    '.sqc__details{margin-top:.875rem}',
    '.sqc__details summary{cursor:pointer;font-size:.9rem;font-weight:600}',
    '.sqc__grid2{display:grid;grid-template-columns:1fr 1fr;gap:.75rem;margin-top:.625rem}',
    '.sqc__grid2 label{display:block;font-size:.8rem;margin-bottom:.25rem}',
    '.sqc__slider{display:flex;align-items:center;gap:.75rem}',
    '.sqc__slider input[type=range]{flex:1;min-width:0;accent-color:var(--color-accent,#e44)}',
    '.sqc__slider .sqc__input{width:88px;flex:0 0 auto}',
    // two columns when each gets >= 260px, otherwise one (no media query needed)
    '.sqc__radio-group--stack{flex-direction:column;flex-wrap:nowrap}',

    // Material + shape tiles
    '.sqc__tiles{display:grid;grid-template-columns:repeat(auto-fit,minmax(72px,1fr));gap:.5rem}',
    '.sqc__tile{position:relative;display:flex;flex-direction:column;align-items:center;gap:.4rem;padding:.6rem .25rem .5rem;border:1.5px solid rgba(0,0,0,.2);border-radius:10px;cursor:pointer;font-size:.74rem;font-weight:600;text-align:center;line-height:1.2;user-select:none;transition:border-color .12s,box-shadow .12s}',
    '.sqc__tile:hover{border-color:rgba(0,0,0,.45)}',
    '.sqc__tile--selected{border-color:var(--color-primary,#000);box-shadow:0 0 0 1px var(--color-primary,#000)}',
    '.sqc__tile--selected::after{content:"";position:absolute;top:6px;right:6px;width:8px;height:8px;border-radius:50%;background:var(--color-primary,#000)}',
    '.sqc__tile:focus-within{outline:2px solid var(--color-accent,#e44);outline-offset:2px}',
    '.sqc__tile svg{width:36px;height:36px;display:block}',
    '.sqc__tile svg .sh{fill:rgba(0,0,0,.05);stroke:currentColor;stroke-width:2;stroke-linejoin:round}',
    '.sqc__chip{width:42px;height:42px;border-radius:10px;position:relative;overflow:hidden;box-shadow:inset 0 0 0 1px rgba(0,0,0,.14),0 1px 2px rgba(0,0,0,.08)}',
    '.sqc__chip::after{content:"";position:absolute;right:0;bottom:0;width:14px;height:14px;background:linear-gradient(135deg,rgba(255,255,255,0) 0 45%,rgba(255,255,255,.85) 46% 100%);box-shadow:-1px -1px 2px rgba(0,0,0,.18);border-top-left-radius:8px}',
    '.sqc__chip--vinyl{background:linear-gradient(135deg,#fff 0%,#dfe3e9 50%,#f7f8fa 100%)}',
    '.sqc__chip--holographic{background:conic-gradient(from 200deg,#7ee8fa,#b794f6,#ff9ecf,#ffe08a,#8bf5c1,#7ee8fa)}',
    '.sqc__chip--clear{background:linear-gradient(120deg,rgba(255,255,255,0) 30%,rgba(255,255,255,.85) 45%,rgba(255,255,255,0) 60%),conic-gradient(#e7e9ee 25%,#fff 0 50%,#e7e9ee 0 75%,#fff 0) 0 0/12px 12px}',
    '.sqc__chip--paper{background:radial-gradient(rgba(120,90,40,.14) .8px,transparent 1px) 0 0/5px 5px,linear-gradient(135deg,#f6eedd,#eadfc6)}',

    // Collapsible setting rows (Corner rounding, Padding)
    '.sqc__row{border:1px solid rgba(0,0,0,.12);border-radius:6px;margin-bottom:.75rem}',
    '.sqc__row>summary{display:flex;justify-content:space-between;align-items:center;gap:.75rem;padding:.7rem .9rem;cursor:pointer;font-weight:600;font-size:.9rem;list-style:none}',
    '.sqc__row>summary::-webkit-details-marker{display:none}',
    '.sqc__row>summary::after{content:"";width:7px;height:7px;border-right:2px solid currentColor;border-bottom:2px solid currentColor;transform:rotate(45deg);margin:-3px 2px 0 .25rem;flex-shrink:0;opacity:.6;transition:transform .15s}',
    '.sqc__row[open]>summary::after{transform:rotate(-135deg);margin-top:3px}',
    '.sqc__row-val{margin-left:auto;font-weight:400;opacity:.65;font-size:.85rem}',
    '.sqc__row-body{padding:0 .9rem .9rem}',
    '.sqc__swatch--none{background:#fff;position:relative}',
    '.sqc__swatch--none::after{content:"";position:absolute;left:4px;right:4px;top:50%;height:2px;background:#be123c;transform:rotate(-45deg)}',
    '.sqc__rm-row{display:flex;flex-wrap:wrap;align-items:center;gap:.5rem}',
    '.sqc__rm-float{position:absolute;top:10px;right:10px;z-index:2;flex-direction:column;align-items:flex-end;gap:.35rem}',
    '.sqc__rm-float .sqc__pane-btn{padding:.35rem .65rem;font-size:.78rem;background:rgba(255,255,255,.94);border:1px solid rgba(0,0,0,.18);box-shadow:0 1px 3px rgba(0,0,0,.15)}',
    '.sqc__rm-detail{margin-top:.5rem}',
    '.sqc__gap{margin-top:.6rem;padding:.7rem .85rem;border:1px solid #ffe082;background:#fff8e1;border-radius:6px;font-size:.85rem;color:#5d4037}',
    '.sqc__gap p{margin:0 0 .5rem}.sqc__gap p:last-child{margin-bottom:0}.sqc__gap .sqc__hint{opacity:.8}',
    '.sqc__rm-detail .sqc__hint{margin:.4rem 0 0}',
    '.sqc__size-row{display:flex;flex-wrap:wrap;align-items:center;gap:.5rem}',

    '.sqc__dim-area{display:flex;flex-direction:column;gap:.375rem}',
    '.sqc__dimensions{display:flex;align-items:center;gap:.5rem;flex-wrap:wrap}',
    '.sqc__dim-input{flex:1;min-width:80px}',
    '.sqc__times{font-size:1.25rem;font-weight:bold;opacity:.5;flex-shrink:0}',
    '.sqc__input{width:100%;padding:.6rem .75rem;border:1px solid rgba(0,0,0,.2);border-radius:4px;font-size:1rem;background:transparent;color:inherit;font-family:inherit;box-sizing:border-box}',
    '.sqc__input:focus{outline:2px solid var(--color-accent,#e44);outline-offset:2px;border-color:transparent}',
    '.sqc__input--qty{width:120px}',

    '.sqc__unit-toggle{display:flex;border:1px solid rgba(0,0,0,.2);border-radius:4px;overflow:hidden;flex-shrink:0;align-self:flex-start}',
    '.sqc__unit-btn{padding:.5rem .75rem;border:none;background:transparent;cursor:pointer;font-size:.85rem;color:inherit;font-family:inherit}',
    '.sqc__unit-btn--active{background:var(--color-primary,#000);color:var(--color-secondary,#fff)}',

    '.sqc__radio-group{display:flex;flex-wrap:wrap;gap:.5rem}',
    '.sqc__radio-label{display:flex;align-items:center;gap:.375rem;cursor:pointer;padding:.45rem .875rem;border:1px solid rgba(0,0,0,.2);border-radius:4px;font-size:.9rem;user-select:none;transition:background .15s,border-color .15s}',
    '.sqc__radio{position:absolute;opacity:0;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap}',
    '.sqc__radio-label--selected{background:var(--color-primary,#000);color:var(--color-secondary,#fff);border-color:var(--color-primary,#000)}',
    '.sqc__radio-label:focus-within{outline:2px solid var(--color-accent,#e44);outline-offset:2px}',

    '.sqc__qty-wrapper{display:flex;align-items:center;gap:.75rem}',
    '.sqc__qty-min{font-size:.8rem;opacity:.6}',

    '.sqc__pricing{border:1px solid rgba(0,0,0,.12);border-radius:6px;padding:1.25rem;margin-bottom:1.25rem;background:rgba(0,0,0,.02)}',
    '.sqc__price-row{display:flex;justify-content:space-between;align-items:center;padding:.3rem 0;font-size:.95rem}',
    '.sqc__price-row--total{font-size:1.1rem;border-top:1px solid rgba(0,0,0,.1);margin-top:.3rem;padding-top:.6rem}',
    '.sqc__discount-badge{display:inline-block;background:var(--color-accent,#e44);color:#fff;padding:.1rem .5rem;border-radius:3px;font-size:.8rem;font-weight:600}',

    '.sqc__error{border-radius:4px;padding:.75rem 1rem;margin-bottom:1rem;background:#fee;color:#c00;border:1px solid #fcc;font-size:.9rem}',

    '.sqc__submit-row{display:flex;gap:.75rem;margin-bottom:1.5rem}',
    '.sqc__submit-row>.sqc__submit{flex:1;width:auto;margin-bottom:0}',
    '.sqc__submit{width:100%;padding:1rem;background:var(--color-accent,#e44);color:#fff;border:none;border-radius:6px;font-size:1rem;font-weight:bold;cursor:pointer;font-family:inherit;transition:opacity .2s}',
    '.sqc__submit:disabled{opacity:.5;cursor:not-allowed}',
    '.sqc__submit--secondary{background:var(--color-primary,#111)}',


    '.sqc__expedited{display:flex;align-items:flex-start;gap:.625rem;padding:.75rem;border:1px solid rgba(0,0,0,.12);border-radius:6px;cursor:pointer;margin-bottom:1.5rem;transition:background .15s,border-color .15s}',
    '.sqc__expedited:hover{border-color:var(--color-accent,#e44);background:rgba(0,0,0,.02)}',
    '.sqc__expedited-check{width:18px;height:18px;accent-color:var(--color-accent,#e44);flex-shrink:0;margin-top:.15rem;cursor:pointer}',
    '.sqc__expedited-body{flex:1}',
    '.sqc__expedited-label{font-weight:600;font-size:.9rem;display:block}',
    '.sqc__expedited-detail{font-size:.8rem;opacity:.65;margin-top:.15rem}',

    '.sqc__design-help{text-align:center;margin-top:1rem;font-size:.875rem;opacity:.75}',
    '.sqc__design-help a{color:var(--color-accent,#e44);font-weight:600}',

    '.sqc__inquiry{border:1px solid rgba(0,0,0,.12);border-radius:6px;padding:1.25rem;margin-top:1rem}',
    '.sqc__inquiry h3{margin:0 0 .75rem;font-size:1rem}',
    '.sqc__inquiry-row{margin-bottom:.875rem}',
    '.sqc__inquiry-row label{display:block;font-weight:600;font-size:.85rem;margin-bottom:.35rem}',
    '.sqc__inquiry-send{width:100%;padding:.875rem;background:var(--color-accent,#e44);color:#fff;border:none;border-radius:6px;font-size:.95rem;font-weight:bold;cursor:pointer;font-family:inherit;transition:opacity .2s}',
    '.sqc__inquiry-send:disabled{opacity:.5;cursor:not-allowed}',

    '.sqc__success{text-align:center;padding:1.5rem;border:1px solid #beb;border-radius:6px;background:#f0fff0}',
    '.sqc__success p{margin:0 0 .5rem;font-weight:600}',
    '.sqc__cart-link{color:var(--color-accent,#e44);font-weight:bold}',

    '@container (max-width:740px){.sqc__stage{flex-direction:column}.sqc__stage-view{order:-1;width:100%;flex:none}.sqc__stage-tools{flex:none;width:100%}}',
    '@media (pointer:coarse),(max-width:740px){#sticker-quote-app .sqc__input,#sticker-quote-app .sqc__stage-tools .sqc__input{font-size:16px}}',
    '@media (pointer:coarse){#sticker-quote-app .sqc__radio-label{min-height:44px}#sticker-quote-app .sqc__stage-tools .sqc__radio-label{min-height:40px}#sticker-quote-app .sqc__unit-btn{min-height:44px;min-width:44px}#sticker-quote-app .sqc__row>summary{min-height:48px}#sticker-quote-app .sqc__file-remove,#sticker-quote-app .sqc__res-dismiss{min-width:44px;min-height:44px;display:inline-flex;align-items:center;justify-content:center}#sticker-quote-app .sqc__pane-btn{min-height:44px}#sticker-quote-app .sqc__slider input[type=range]{min-height:32px}#sticker-quote-app .sqc__input{min-height:44px}#sticker-quote-app .sqc__check{min-height:44px}#sticker-quote-app .sqc__check input{width:22px;height:22px}#sticker-quote-app .sqc__swatch{width:36px;height:36px}#sticker-quote-app .sqc__slider .sqc__input{padding-top:.7rem;padding-bottom:.7rem}}',
    '@media(max-width:480px){.sqc__grid2{grid-template-columns:1fr}.sqc__stage-tools .sqc__radio-group{display:grid;grid-template-columns:1fr 1fr}.sqc__stage-tools .sqc__radio-group>.sqc__radio-label:last-child:nth-child(odd){grid-column:1/-1}.sqc__stage-tools .sqc__radio-label{font-size:.78rem;padding:.4rem .5rem}}',
  ].join('');

  // ─── Order Manager Storage ────────────────────────────────────────────────────

  function loadRuns() {
    try {
      var raw = localStorage.getItem(ORDER_STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  function saveRuns(runs) {
    try {
      localStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(runs));
    } catch (e) {}
  }

  function loadDraftOrder() {
    try {
      var raw = localStorage.getItem(DRAFT_ORDER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function saveDraftOrder(data) {
    try {
      localStorage.setItem(DRAFT_ORDER_KEY, JSON.stringify(data));
    } catch (e) {}
  }

  function clearDraftOrder() {
    try {
      localStorage.removeItem(DRAFT_ORDER_KEY);
    } catch (e) {}
  }

  function clearAllRuns() {
    try {
      localStorage.removeItem(ORDER_STORAGE_KEY);
      localStorage.removeItem(DRAFT_ORDER_KEY);
    } catch (e) {}
  }

  // ─── Order Manager UI ─────────────────────────────────────────────────────────

  function buildOrderManagerHTML(cfg) {
    return [
      '<div id="sqc-order-manager" style="margin-top:2rem;">',
        '<h3 style="margin-bottom:0.75rem;">Your Order Runs</h3>',
        '<div id="sqc-runs-list"></div>',
        '<div id="sqc-order-actions" style="margin-top:1rem;display:none;">',
          '<div id="sqc-guest-email-row" style="margin-bottom:0.75rem;display:none;">',
            '<label style="display:block;font-weight:600;font-size:.875rem;margin-bottom:.35rem;" for="sqc-guest-email">Email Address <span style="color:var(--color-accent,#e44)" aria-hidden="true">*</span></label>',
            '<input type="email" id="sqc-guest-email" class="sqc__input" autocomplete="email" placeholder="your@email.com">',
            '<p style="font-size:.8rem;opacity:.65;margin:.25rem 0 0">We\'ll send your order confirmation to this address.</p>',
          '</div>',
          '<p id="sqc-retention-note" hidden style="font-size:.8rem;opacity:.65;margin:0 0 .75rem"></p>',
          '<label class="sqc__expedited">',
            '<input type="checkbox" id="sqc-artwork-approval" class="sqc__expedited-check" aria-describedby="sqc-artwork-approval-text">',
            '<span class="sqc__expedited-body">',
              '<span class="sqc__expedited-label">Artwork Approval <span class="sqc__req" aria-hidden="true">*</span></span>',
              '<span class="sqc__expedited-detail" id="sqc-artwork-approval-text">' + escapeHtml(cfg.artworkApproval) + '</span>',
            '</span>',
          '</label>',
          '<button type="button" id="sqc-checkout-all" class="sqc__submit" style="width:auto;padding:.75rem 1.5rem;">Proceed to Checkout</button>',
          '<button type="button" id="sqc-clear-order" style="margin-left:1rem;background:none;border:none;cursor:pointer;color:var(--color-accent,#e44);font-family:inherit;font-size:.875rem;text-decoration:underline;">Clear All</button>',
        '</div>',
        '<div id="sqc-draft-resume" style="display:none;margin-top:1rem;padding:1rem;border:1px solid #ddd;border-radius:4px;">',
          '<p style="margin:0 0 .75rem;">You have an order in progress.</p>',
          '<button type="button" id="sqc-resume-btn" class="sqc__submit" style="width:auto;padding:.75rem 1.5rem;">Resume Checkout</button>',
          '<button type="button" id="sqc-discard-btn" style="margin-left:1rem;background:none;border:none;cursor:pointer;color:var(--color-accent,#e44);font-family:inherit;font-size:.875rem;text-decoration:underline;">Discard &amp; Start Over</button>',
        '</div>',
      '</div>',
    ].join('');
  }

  // ─── Template ─────────────────────────────────────────────────────────────────

  function radioHTML(name, value, label, checked) {
    return '<label class="sqc__radio-label' + (checked ? ' sqc__radio-label--selected' : '') + '">' +
      '<input type="radio" name="' + name + '" value="' + value + '" class="sqc__radio"' + (checked ? ' checked' : '') + '>' +
      '<span>' + label + '</span></label>';
  }

  // Picture tile (material / shape). The radio stays in the DOM, visually hidden.
  function tileHTML(name, value, label, checked, graphic) {
    return '<label class="sqc__tile' + (checked ? ' sqc__tile--selected' : '') + '">' +
      '<input type="radio" name="' + name + '" value="' + value + '" class="sqc__radio"' + (checked ? ' checked' : '') + '>' +
      graphic + '<span>' + label + '</span></label>';
  }

  var SHAPE_TILE_SVG = {
    rect:   '<svg viewBox="0 0 40 40" aria-hidden="true"><rect class="sh" x="5" y="11" width="30" height="18" rx="3"/></svg>',
    square: '<svg viewBox="0 0 40 40" aria-hidden="true"><rect class="sh" x="8" y="8" width="24" height="24" rx="3"/></svg>',
    circle: '<svg viewBox="0 0 40 40" aria-hidden="true"><circle class="sh" cx="20" cy="20" r="13"/></svg>',
    diecut: '<svg viewBox="0 0 40 40" aria-hidden="true"><path class="sh" d="M20 5.5c3 0 4.2 3.6 7 4.6 3 1.1 7.2-.2 8 3 .8 3.2-2.8 5.4-2.8 8.2 0 3 3.8 5.4 2 8-1.8 2.6-5.8.6-8.4 2.2-2.6 1.6-2.8 5.5-5.8 5.5s-3.2-3.9-5.8-5.5C11.6 30 7.6 32 5.8 29.4c-1.8-2.6 2-5 2-8 0-2.8-3.6-5-2.8-8.2.8-3.2 5-1.9 8-3 2.8-1-.1-4.7 7-4.7z"/></svg>'
  };
  var SHAPE_TILE_LABEL = { rect: 'Rectangle', square: 'Square', circle: 'Circle', diecut: 'Die-cut' };

  // Range slider + linked text box (text so the corner box can read "Square").
  // Range, step and value are set in linkSlider().
  function sliderHTML(id, label) {
    return '<label class="sqc__label" for="' + id + '-num">' + label + ' (mm)</label>' +
      '<div class="sqc__slider">' +
        '<input type="range" id="' + id + '" step="0.5" aria-label="' + label + '">' +
        '<input type="text" inputmode="decimal" id="' + id + '-num" class="sqc__input" autocomplete="off">' +
      '</div>';
  }

  function numFieldHTML(id, label, value, min, max, step) {
    return '<div><label for="' + id + '">' + label + '</label>' +
      '<input type="number" id="' + id + '" class="sqc__input" value="' + value + '" min="' + min + '"' +
      (max !== '' ? ' max="' + max + '"' : '') + ' step="' + step + '"></div>';
  }

  function buildHTML(cfg) {
    var expeditedDays  = cfg.turnaround - cfg.expeditedReduction;
    var expeditedLabel = expeditedDays + '&ndash;' + (expeditedDays + 2) + ' business days';
    var maxDiam        = Math.min(cfg.maxWidth, cfg.maxHeight);

    return [
      '<h2 class="sqc__title">Get an Instant Quote</h2>',

      // ── Content mode ──
      '<div class="sqc__field">',
        '<fieldset class="sqc__fieldset">',
          '<legend class="sqc__label">Artwork <span class="sqc__req" aria-hidden="true">*</span></legend>',
          '<div class="sqc__radio-group" id="sqc-mode-group">',
            radioHTML('sqc-mode', 'logo', 'Upload a logo', true),
            radioHTML('sqc-mode', 'text', 'Text', false),
          '</div>',
        '</fieldset>',
      '</div>',

      // ── Text ──
      '<div class="sqc__field" id="sqc-text-section" hidden>',
        '<label class="sqc__label" for="sqc-text">Sticker text <span class="sqc__req" aria-hidden="true">*</span></label>',
        '<textarea id="sqc-text" class="sqc__input sqc__textarea" placeholder="Type your sticker text…"></textarea>',
        '<div class="sqc__checks">',
          '<label class="sqc__check"><input type="checkbox" id="sqc-text-upper" checked> Force ALL CAPS</label>',
          '<label class="sqc__check"><input type="checkbox" id="sqc-text-bold" checked> Bold</label>',
        '</div>',
        '<span class="sqc__label" style="margin-top:.875rem">Text colour</span>',
        '<div class="sqc__swatches" id="sqc-text-colours" role="radiogroup" aria-label="Text colour"></div>',
        '<details class="sqc__details">',
          '<summary>Text settings</summary>',
          '<div class="sqc__grid2">',
            numFieldHTML('sqc-text-max',      'Max font size (pt)',   60,  4,   '',  1),
            numFieldHTML('sqc-text-min',      'Min font size (pt)',   6,   2,   '',  1),
            numFieldHTML('sqc-text-leading',  'Line spacing (×)',     1.2, 0.8, '',  0.05),
            numFieldHTML('sqc-text-condense', 'Horizontal scale (%)', 100, 50,  150, 1),
          '</div>',
          '<p class="sqc__hint">Text is sized automatically to fit inside the safe margin. Horizontal scale condenses or stretches letters sideways only (100% = normal).</p>',
        '</details>',
      '</div>',

      // ── Logo file ──
      '<div class="sqc__field" id="sqc-logo-section">',
        '<label class="sqc__label" id="sqc-file-label">Artwork File <span class="sqc__req" aria-hidden="true">*</span></label>',
        '<div class="sqc__dropzone" id="sqc-dropzone" role="button" tabindex="0" aria-labelledby="sqc-file-label">',
          '<div class="sqc__dropzone-content">',
            '<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">',
              '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>',
              '<polyline points="17 8 12 3 7 8"/>',
              '<line x1="12" y1="3" x2="12" y2="15"/>',
            '</svg>',
            '<p class="sqc__dropzone-primary">Drag &amp; drop your artwork here</p>',
            '<p class="sqc__dropzone-secondary">or <span class="sqc__browse-link">click to browse</span></p>',
            '<p class="sqc__dropzone-hint">PNG, JPG, SVG &nbsp;&middot;&nbsp; Max 10&thinsp;MB</p>',
          '</div>',
          '<input type="file" id="sqc-file-input" class="sqc__file-input" accept=".png,.jpg,.jpeg,.svg" aria-label="Select artwork file">',
        '</div>',
        '<div class="sqc__file-preview" id="sqc-file-preview" hidden>',
          '<span class="sqc__file-name" id="sqc-file-name"></span>',
          '<button type="button" class="sqc__pane-btn sqc__pane-btn--secondary sqc__pane-btn--sm" id="sqc-logo-reset" disabled title="Undo moving, resizing and rotating">Reset</button>',
          '<button type="button" class="sqc__file-remove" id="sqc-file-remove" aria-label="Remove selected file">&times;</button>',
        '</div>',
        '<div class="sqc__res-warning" id="sqc-res-warning" hidden>',
          '<span>For best print quality, we recommend uploading an image of at least 12 megapixels resolution.</span>',
          '<button type="button" class="sqc__res-dismiss" id="sqc-res-dismiss" aria-label="Dismiss warning">&times;</button>',
        '</div>',
        '<p class="sqc__edit-note" id="sqc-edit-note" hidden>Keeping the artwork from this run. Upload a new file to replace it — you’ll need to if you change the material, shape, size, corner rounding, padding, margin or bleed.</p>',
      '</div>',

      // ── Material ──
      '<div class="sqc__field">',
        '<fieldset class="sqc__fieldset">',
          '<legend class="sqc__label">Material</legend>',
          '<div class="sqc__tiles" id="sqc-material-group" role="radiogroup" aria-label="Material">',
            ['vinyl', 'holographic', 'clear', 'paper'].map(function (m) {
              return tileHTML('sqc-material', m, MATERIAL_NAMES[m], m === 'vinyl', '<span class="sqc__chip sqc__chip--' + m + '"></span>');
            }).join(''),
          '</div>',
        '</fieldset>',
        '<p class="sqc__hint" id="sqc-material-note"></p>',
        '<div class="sqc__gap" id="sqc-nowhite-note" role="note" hidden></div>',
      '</div>',

      // ── Shape ──
      '<div class="sqc__field">',
        '<fieldset class="sqc__fieldset">',
          '<legend class="sqc__label">Shape <span class="sqc__req" aria-hidden="true">*</span></legend>',
          '<div class="sqc__tiles" id="sqc-shape-group" role="radiogroup" aria-label="Shape">',
            ['rect', 'square', 'circle', 'diecut'].map(function (sh) {
              return tileHTML('sqc-shape', sh, SHAPE_TILE_LABEL[sh], sh === 'rect', SHAPE_TILE_SVG[sh]);
            }).join(''),
          '</div>',
        '</fieldset>',
        '<p class="sqc__hint" id="sqc-diecut-note" hidden>The cut line follows your artwork. Pick the largest size it may be — the artwork is scaled to fill it — then drag the corner handle to make it smaller if you like. It can’t grow past the size you pick, so choose a bigger size to go larger. You pay for the real size of the finished sticker (shown on the preview), so the price updates as you resize.</p>',
      '</div>',

      // ── Design preview: the canvas is the print-ready artwork ──
      '<div id="sqc-design-pane" class="sqc__design-pane">',
        '<p class="sqc__pane-label">Preview — this is your print-ready artwork</p>',
        '<div class="sqc__stage-wrap">',
        '<div class="sqc__stage">',
          '<div class="sqc__stage-tools">',
      // ── Size: presets + unit, then dimensions ──
      '<details class="sqc__row" id="sqc-size-field" open>',
        '<summary>Size <span class="sqc__req" aria-hidden="true">*</span> <span class="sqc__row-val" id="sqc-size-sum"></span></summary>',
        '<div class="sqc__row-body">',
        '<div class="sqc__size-row">',
          '<div class="sqc__radio-group" id="sqc-preset-group" role="radiogroup" aria-label="Size preset"></div>',
          '<div class="sqc__unit-toggle" role="group" aria-label="Unit">',
            '<button type="button" class="sqc__unit-btn sqc__unit-btn--active" data-unit="mm" aria-pressed="true">mm</button>',
            '<button type="button" class="sqc__unit-btn" data-unit="in" aria-pressed="false">in</button>',
          '</div>',
        '</div>',
        '<div class="sqc__dim-area" style="margin-top:.75rem">',
          '<div id="sqc-rect-dims" class="sqc__dimensions">',
            '<div class="sqc__dim-input">',
              '<input type="number" id="sqc-width" class="sqc__input" min="10" step="1" placeholder="Width" aria-label="Width" aria-describedby="sqc-dim-hint">',
            '</div>',
            '<span class="sqc__times" aria-hidden="true">&times;</span>',
            '<div class="sqc__dim-input">',
              '<input type="number" id="sqc-height" class="sqc__input" min="10" step="1" placeholder="Height" aria-label="Height" aria-describedby="sqc-dim-hint">',
            '</div>',
          '</div>',
          '<div id="sqc-circ-dims" class="sqc__dimensions" hidden>',
            '<div class="sqc__dim-input">',
              '<input type="number" id="sqc-diameter" class="sqc__input" min="10" step="1" placeholder="Diameter" aria-label="Diameter" aria-describedby="sqc-dim-hint">',
            '</div>',
          '</div>',
        '</div>',
        '<p class="sqc__hint" id="sqc-dim-hint">Max: ' + cfg.maxWidth + '&thinsp;mm &times; ' + cfg.maxHeight + '&thinsp;mm</p>',
      '</div>',
      '</details>',

      // ── Corner rounding (rectangles and squares only) ──
      '<details class="sqc__row" id="sqc-corner-field">',
        '<summary>Corner rounding <span class="sqc__row-val" id="sqc-corner-sum"></span></summary>',
        '<div class="sqc__row-body">', sliderHTML('sqc-corner', 'Corner rounding'), '</div>',
      '</details>',

      // ── Padding (die-cut only): the gap between the artwork and the cut line ──
      '<details class="sqc__row" id="sqc-pad-field" hidden>',
        '<summary>Padding <span class="sqc__row-val" id="sqc-pad-sum"></span></summary>',
        '<div class="sqc__row-body">',
          sliderHTML('sqc-pad', 'Padding'),
          '<span class="sqc__label" style="margin-top:.75rem">Padding colour</span>',
          '<div class="sqc__swatches" id="sqc-pad-colours" role="radiogroup" aria-label="Padding colour"></div>',
          '<p class="sqc__hint">Printed between your artwork and the cut line. Choose None to leave it unprinted.</p>',
          '<p class="sqc__hint" id="sqc-pad-bleed-note" hidden>With no padding the cut line sits right on your artwork’s edge, so we carry its outer colours out past the cut line as bleed — no unprinted edge shows if the cut shifts slightly. You can set how far in Fill Print below.</p>',
        '</div>',
      '</details>',

      // ── Safe margin ──
      '<details class="sqc__row" id="sqc-margin-field">',
        '<summary>Safe margin <span class="sqc__row-val" id="sqc-margin-sum"></span></summary>',
        '<div class="sqc__row-body">',
          sliderHTML('sqc-margin', 'Safe margin'),
          '<p class="sqc__hint">Keeps your text clear of the cut line.</p>',
        '</div>',
      '</details>',

      // ── Bleed: needed whenever something prints out to the cut line ──
      '<details class="sqc__row" id="sqc-bleed-field" hidden>',
        '<summary>Fill Print <span class="sqc__row-val" id="sqc-bleed-sum"></span></summary>',
        '<div class="sqc__row-body">',
          '<label class="sqc__check" id="sqc-fullfill-row" hidden><input type="checkbox" id="sqc-fullfill"> My artwork fills the whole sticker (run it out to the edge)</label>',
          '<p class="sqc__hint" id="sqc-fullfill-hint" hidden>Your artwork is scaled up to the dashed bleed line so nothing is left bare at the cut line. Drag the corner handle to resize it.</p>',
          '<div id="sqc-bleed-body">',
            sliderHTML('sqc-bleed', 'Fill print'),
            '<p class="sqc__hint">A bleed is required to avoid any white areas around the edge of your print. Whatever is printed out to the cut line (a fill colour, padding colour, a full-fill design, or your artwork’s own edge when die-cut padding is None) is carried this far past it, so no white edge shows if the cut shifts slightly. We recommend at least ' + BLEED_DEFAULT_MM + ' mm. Shown as a dashed guide — the extra isn’t part of the sticker.</p>',
          '</div>',
          '<p class="sqc__hint" id="sqc-bleed-none">Nothing prints out to the cut line yet, so there is nothing to fill. A bleed (we recommend at least ' + BLEED_DEFAULT_MM + ' mm) switches on as soon as you add a fill colour or full-fill design — or set die-cut padding to None.</p>',
        '</div>',
      '</details>',

      // ── Background fill (logo colour behind a rectangle/square/circle logo) ──
      '<details class="sqc__row" id="sqc-bg-box" hidden>',
        '<summary>Fill background <span class="sqc__row-val" id="sqc-bg-sum">Off</span></summary>',
        '<div class="sqc__row-body">',
          '<label class="sqc__check"><input type="checkbox" id="sqc-bg-enabled"> Fill background with a colour from the logo</label>',
          '<p class="sqc__hint">Fills any gap between your logo and the cut line instead of leaving it white.</p>',
          '<div id="sqc-bg-controls" hidden>',
            '<div class="sqc__swatches" id="sqc-bg-swatches"></div>',
            '<button type="button" id="sqc-eyedropper" class="sqc__pane-btn sqc__pane-btn--secondary" style="margin-top:.625rem" aria-pressed="false">Pick exact colour from image</button>',
            '<div class="sqc__current-colour"><span class="sqc__current-swatch" id="sqc-bg-current-swatch"></span><span id="sqc-bg-current-hex">No colour selected</span></div>',
          '</div>',
        '</div>',
      '</details>',
          '</div>',
          '<div class="sqc__stage-view">',
            '<canvas id="sqc-pane-canvas" class="sqc__pane-canvas" width="' + PANE_W + '" height="' + PANE_H + '" aria-label="Sticker artwork preview"></canvas>',
            '<p class="sqc__empty" id="sqc-empty" hidden>Upload your logo to place it on the sticker</p>',
            '<div class="sqc__rm" id="sqc-rmbg-box" hidden>',
              '<div class="sqc__rm-row sqc__rm-float">',
                '<button type="button" id="sqc-rmbg-btn" class="sqc__pane-btn sqc__pane-btn--secondary">Remove background</button>',
                '<button type="button" id="sqc-rmbg-restore" class="sqc__pane-btn sqc__pane-btn--secondary" hidden>Restore original</button>',
              '</div>',
              '<div class="sqc__rm-detail">',
                '<div id="sqc-rmbg-tune" hidden>',
                  '<label class="sqc__label" for="sqc-rmbg-tol">Sensitivity <span id="sqc-rmbg-val" style="font-weight:400">' + RMBG_DEFAULT_TOL + '</span></label>',
                  '<input type="range" id="sqc-rmbg-tol" min="8" max="120" step="1" value="' + RMBG_DEFAULT_TOL + '" style="width:100%;accent-color:var(--color-accent,#e44)">',
                '</div>',
                '<div id="sqc-rm-extra">',
                  '<label class="sqc__check" style="margin-top:.5rem"><input type="checkbox" id="sqc-rm-inner"> Also clear gaps inside the design (same colour as the background)</label>',
                  '<button type="button" id="sqc-rm-erase" class="sqc__pane-btn sqc__pane-btn--secondary" style="margin-top:.5rem" aria-pressed="false">Erase a spot</button>',
                  '<button type="button" id="sqc-rm-erase-undo" class="sqc__pane-btn sqc__pane-btn--secondary" style="margin-top:.5rem;margin-left:.4rem" hidden>Undo last spot</button>',
                '</div>',
                '<label class="sqc__check" id="sqc-white-row" style="margin-top:.5rem" hidden><input type="checkbox" id="sqc-white-bg"> Treat white as background</label>',
                '<p class="sqc__hint" id="sqc-rmbg-hint"></p>',
              '</div>',
            '</div>',
            '<div class="sqc__gap" id="sqc-gap-box" hidden>',
              '<p id="sqc-gap-text"></p>',
              '<div class="sqc__rm-row">',
                '<button type="button" id="sqc-stretch-btn" class="sqc__pane-btn sqc__pane-btn--secondary">Stretch the edge pixels</button>',
                '<button type="button" id="sqc-gapfill-btn" class="sqc__pane-btn sqc__pane-btn--secondary">Fill with a colour from my logo</button>',
                '<button type="button" id="sqc-unstretch-btn" class="sqc__pane-btn sqc__pane-btn--secondary" hidden>Undo stretch</button>',
              '</div>',
              '<p class="sqc__hint" id="sqc-gap-hint">Stretching repeats the outermost row of pixels out to the bleed line, so it works best when your edges are a plain colour. A fill colour is printed behind your artwork out to the bleed line.</p>',
            '</div>',
          '</div>',
        '</div>',
        '</div>',
        '<p class="sqc__pane-help" id="sqc-pane-help"></p>',
        '<p class="sqc__pane-status" id="sqc-pane-status" role="status" aria-live="polite" hidden></p>',
      '</div>',

      // ── Quantity ──
      '<div class="sqc__field">',
        '<label class="sqc__label" for="sqc-qty">Quantity <span class="sqc__req" aria-hidden="true">*</span></label>',
        '<div class="sqc__qty-wrapper">',
          '<input type="number" id="sqc-qty" class="sqc__input sqc__input--qty" min="' + cfg.minQty + '" step="1" value="' + cfg.minQty + '" aria-describedby="sqc-qty-min">',
          '<span class="sqc__qty-min" id="sqc-qty-min">Minimum: ' + cfg.minQty + '</span>',
        '</div>',
      '</div>',

      '<label class="sqc__expedited" id="sqc-expedited-label">',
        '<input type="checkbox" id="sqc-expedited" class="sqc__expedited-check" aria-describedby="sqc-expedited-detail">',
        '<span class="sqc__expedited-body">',
          '<span class="sqc__expedited-label">Expedited Delivery (+' + cfg.expeditedSurcharge + '%)</span>',
          '<span class="sqc__expedited-detail" id="sqc-expedited-detail">Receive in ' + expeditedLabel + ' instead of ' + cfg.turnaround + '&ndash;' + (cfg.turnaround + 2) + ' business days</span>',
        '</span>',
      '</label>',

      '<div class="sqc__pricing" id="sqc-pricing" hidden>',
        '<div class="sqc__price-row"><span>Unit Price</span><strong id="sqc-unit-price">&mdash;</strong></div>',
        '<div class="sqc__price-row sqc__price-row--total"><span>Total Price</span><strong id="sqc-total-price">&mdash;</strong></div>',
        '<div class="sqc__price-row" id="sqc-priced-size" hidden><span>Priced on size</span><span id="sqc-priced-size-val"></span></div>',
        '<div class="sqc__price-row" id="sqc-material-row" hidden><span>Material</span><span id="sqc-material-price"></span></div>',
        '<div class="sqc__price-row" id="sqc-discount-row" hidden><span>Bulk Discount</span><span class="sqc__discount-badge" id="sqc-discount"></span></div>',
        '<div class="sqc__price-row"><span>Est. Delivery</span><span id="sqc-delivery">' + cfg.turnaround + '&ndash;' + (cfg.turnaround + 2) + ' business days</span></div>',
      '</div>',

      '<div class="sqc__error" id="sqc-error" role="alert" aria-live="polite" hidden></div>',
      '<div class="sqc__submit-row">',
        '<button type="button" class="sqc__submit" id="sqc-add-run">Add Run</button>',
      '</div>',
      '<div class="sqc__design-help">Need help with your design? <a href="' + cfg.contactUrl + '">Get in touch</a></div>',
      buildOrderManagerHTML(cfg),

      '<div class="sqc__inquiry" id="sqc-inquiry" hidden>',
        '<h3>Send Us Your Order</h3>',
        '<p style="font-size:.875rem;margin:0 0 1rem;opacity:.75">We\'ll confirm your quote and send a payment link within 1 business day.</p>',
        '<div class="sqc__inquiry-row"><label for="sqc-inq-name">Your Name</label><input type="text" id="sqc-inq-name" class="sqc__input" autocomplete="name" required></div>',
        '<div class="sqc__inquiry-row"><label for="sqc-inq-email">Email Address</label><input type="email" id="sqc-inq-email" class="sqc__input" autocomplete="email" required></div>',
        '<div class="sqc__error" id="sqc-inq-error" role="alert" aria-live="polite" hidden></div>',
        '<button type="button" class="sqc__inquiry-send" id="sqc-inq-send">Send Enquiry</button>',
      '</div>',

      '<div class="sqc__success" id="sqc-inq-success" hidden>',
        '<p>Enquiry sent!</p>',
        '<p style="font-size:.875rem;font-weight:normal">We\'ll be in touch within 1 business day to confirm your order.</p>',
      '</div>',
    ].join('');
  }

  // ─── Validation ──────────────────────────────────────────────────────────────

  function validateFile(file) {
    var ext = getExt(file.name);
    if (!ACCEPTED_EXTS[ext]) return 'File type not accepted. Use PNG, JPG or SVG.';
    if (file.type && !ACCEPTED_MIME[file.type]) return 'File type not accepted. Use PNG, JPG or SVG.';
    if (file.size > MAX_BYTES) return 'File is too large. Maximum size is 10 MB.';
    return null;
  }

  // reuseEpsUrl: an edited logo run's existing EPS, when still valid for its geometry.
  function validateState(state, cfg, pane, reuseEpsUrl) {
    if (!pane.available) return 'The artwork designer failed to load. Please refresh the page.';
    if (state.mode === 'text') {
      if (!pane.hasText()) return 'Please enter the text for your sticker.';
      if (!pane.fontsReady()) return 'The text preview is still loading — please try again in a moment.';
    } else if (!state.image && !reuseEpsUrl) {
      return state.editEps && state.editEps.url
        ? 'You changed the size or shape of this run — please re-upload your artwork so it can be regenerated.'
        : 'Please upload your artwork.';
    }
    // die-cut: the outline is built from the artwork, so it can be blocked until the artwork gives it one
    if (!reuseEpsUrl && state.shape === 'diecut') {
      var blocked = pane.exportBlocked();
      if (blocked) return blocked;
    }
    // full-fill artwork that leaves a gap needs its edges stretched, or a fill colour behind it
    if (!reuseEpsUrl && state.mode === 'logo' && state.shape !== 'diecut' && state.image && pane.gapBlocked && pane.gapBlocked()) {
      return 'Your full-fill artwork doesn’t reach every edge of the sticker. Stretch its edge pixels or fill the gap with a colour from your logo (box under the preview), or untick “My artwork fills the whole sticker”.';
    }
    var maxDiam = Math.min(cfg.maxWidth, cfg.maxHeight);
    if (state.shape === 'circle' || state.shape === 'square') {
      var dimName = state.shape === 'circle' ? 'Diameter' : 'Side';
      if (!state.widthMm || state.widthMm < 10) return dimName + ' must be at least 10 mm.';
      if (state.widthMm > maxDiam) return dimName + ' must be at most ' + maxDiam + ' mm.';
    } else {
      if (!state.widthMm || state.widthMm < 10) return 'Width must be at least 10 mm.';
      if (state.widthMm > cfg.maxWidth) return 'Width must be at most ' + cfg.maxWidth + ' mm.';
      if (!state.heightMm || state.heightMm < 10) return 'Height must be at least 10 mm.';
      if (state.heightMm > cfg.maxHeight) return 'Height must be at most ' + cfg.maxHeight + ' mm.';
    }
    if (!state.qty || state.qty < cfg.minQty) return 'Minimum quantity is ' + cfg.minQty + '.';
    return null;
  }

  // ─── File upload ─────────────────────────────────────────────────────────────

  function uploadFile(file, workerUrl) {
    if (!workerUrl) return Promise.resolve('file://pending-upload/' + encodeURIComponent(file.name));
    var fd = new FormData();
    fd.append('file', file);
    return fetch(workerUrl, { method: 'POST', body: fd })
      .then(function (r) {
        if (!r.ok) throw new Error('Artwork upload failed (' + r.status + '). Please try again.');
        return r.json();
      })
      .then(function (d) { return d.url; });
  }

  // ─── Checkout via Draft Order ─────────────────────────────────────────────────

  // The worker only knows rectangular, square and circular runs and prices from the run's quote.
  // So a die-cut run goes through as a rectangle of its real outline size, and its true shape and material
  // travel in extra fields (and in the artwork file's name and EPS header) for the production team.
  function toWorkerItem(run) {
    var item = {};
    Object.keys(run).forEach(function (k) { item[k] = run[k]; });
    item.shape = run.shape === 'diecut' ? 'rect' : run.shape;
    item.designShape = SHAPE_NAMES[run.shape] || SHAPE_NAMES.rect;
    item.material = MATERIAL_NAMES[run.material] ? run.material : 'vinyl';
    item.materialLabel = MATERIAL_NAMES[item.material];
    return item;
  }

  function createOrder(items, email, customerId, cfg) {
    var payload = { items: items.map(toWorkerItem) };
    if (email) payload.email = email;
    if (customerId) payload.customerId = customerId;
    // The worker refuses orders without it, and stores the wording as accepted.
    payload.artworkApproval = { text: cfg.artworkApproval, at: new Date().toISOString() };
    return fetch(cfg.workerUrl + '/order/create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(function (r) {
        // The worker answers errors in plain text. Its 400s are written for the
        // customer; 5xx bodies carry raw Shopify detail, so they get a generic line.
        if (!r.ok) return r.text().then(function (t) {
          throw new Error(r.status === 400 && t ? t + '.' : 'Order creation failed. Please try again.');
        });
        return r.json();
      })
      .then(function (d) {
        if (!d.invoiceUrl) throw new Error('No invoice URL returned.');
        return d; // { invoiceUrl, expiresAt, retentionDays }
      });
  }

  // ─── UI helpers ──────────────────────────────────────────────────────────────

  function syncRadioStyles(group) {
    group.querySelectorAll('.sqc__radio-label').forEach(function (label) {
      label.classList.toggle('sqc__radio-label--selected', label.querySelector('.sqc__radio').checked);
    });
    group.querySelectorAll('.sqc__tile').forEach(function (tile) {
      tile.classList.toggle('sqc__tile--selected', tile.querySelector('.sqc__radio').checked);
    });
  }

  function updatePricing(root, state, cfg) {
    var el      = root.querySelector('#sqc-pricing');
    var hasDims = state.shape === 'circle' || state.shape === 'square' ? !!state.widthMm : (!!state.widthMm && !!state.heightMm);
    if (!hasDims || !state.qty) { el.hidden = true; return; }
    var q = quoteFor(state, cfg);
    if (!q) { el.hidden = true; return; }
    var sizeRow = root.querySelector('#sqc-priced-size');
    if (state.shape === 'diecut') {
      var pd = pricedDims(state);
      sizeRow.hidden = false;
      root.querySelector('#sqc-priced-size-val').textContent = pd.w + ' \u00d7 ' + pd.h + ' mm' + (state.cutMm ? '' : ' (largest size)');
    } else {
      sizeRow.hidden = true;
    }
    var matRow = root.querySelector('#sqc-material-row');
    if (q.materialPct) {
      matRow.hidden = false;
      root.querySelector('#sqc-material-price').textContent = MATERIAL_NAMES[state.material] + ' (' + (q.materialPct > 0 ? '+' : '') + q.materialPct + '%)';
    } else {
      matRow.hidden = true;
    }
    el.hidden = false;
    root.querySelector('#sqc-unit-price').textContent  = fmtCurrency(q.unitPrice, cfg.currency);
    root.querySelector('#sqc-total-price').textContent = fmtCurrency(q.totalPrice, cfg.currency);
    var discRow = root.querySelector('#sqc-discount-row');
    if (q.discount > 0) {
      discRow.hidden = false;
      root.querySelector('#sqc-discount').textContent = q.discount + '% off';
    } else {
      discRow.hidden = true;
    }
    root.querySelector('#sqc-delivery').textContent = q.days + '–' + (q.days + 2) + ' business days';
  }

  function showError(root, msg) {
    var el = root.querySelector('#sqc-error');
    el.hidden = !msg;
    if (msg) el.textContent = msg;
  }

  // ─── Design pane ─────────────────────────────────────────────────────────────
  // Wraps a StroopEpsDesigner (theme/assets/eps-designer.js) over the pane
  // canvas. The designer owns placement, preview and EPS export; this owns the
  // surrounding controls (text settings, background fill) and status copy.

  function setupDesignPane(root, state, cfg) {
    var paneEl        = root.querySelector('#sqc-design-pane');
    var canvasEl      = root.querySelector('#sqc-pane-canvas');
    var helpEl        = root.querySelector('#sqc-pane-help');
    var statusEl      = root.querySelector('#sqc-pane-status');
    var emptyEl       = root.querySelector('#sqc-empty');
    var bgBox         = root.querySelector('#sqc-bg-box');
    var bgEnabledEl   = root.querySelector('#sqc-bg-enabled');
    var bgControls    = root.querySelector('#sqc-bg-controls');
    var bgSwatchesEl  = root.querySelector('#sqc-bg-swatches');
    var eyedropperBtn = root.querySelector('#sqc-eyedropper');
    var bgCurSwatch   = root.querySelector('#sqc-bg-current-swatch');
    var bgCurHex      = root.querySelector('#sqc-bg-current-hex');
    var bgSumEl       = root.querySelector('#sqc-bg-sum');
    var rmBox         = root.querySelector('#sqc-rmbg-box');
    var gapBox        = root.querySelector('#sqc-gap-box');
    var gapText       = root.querySelector('#sqc-gap-text');
    var gapHint       = root.querySelector('#sqc-gap-hint');
    var stretchBtn    = root.querySelector('#sqc-stretch-btn');
    var gapFillBtn    = root.querySelector('#sqc-gapfill-btn');
    var unstretchBtn  = root.querySelector('#sqc-unstretch-btn');
    var rmBtn         = root.querySelector('#sqc-rmbg-btn');
    var rmRestoreBtn  = root.querySelector('#sqc-rmbg-restore');
    var rmTune        = root.querySelector('#sqc-rmbg-tune');
    var rmTol         = root.querySelector('#sqc-rmbg-tol');
    var rmVal         = root.querySelector('#sqc-rmbg-val');
    var rmHint        = root.querySelector('#sqc-rmbg-hint');
    var rmExtra       = root.querySelector('#sqc-rm-extra');
    var rmInnerEl     = root.querySelector('#sqc-rm-inner');
    var rmEraseBtn    = root.querySelector('#sqc-rm-erase');
    var rmEraseUndo   = root.querySelector('#sqc-rm-erase-undo');
    var whiteRow      = root.querySelector('#sqc-white-row');
    var whiteEl       = root.querySelector('#sqc-white-bg');
    var resetBtn      = root.querySelector('#sqc-logo-reset');
    var textEl        = root.querySelector('#sqc-text');
    var upperEl       = root.querySelector('#sqc-text-upper');
    var boldEl        = root.querySelector('#sqc-text-bold');
    var coloursEl     = root.querySelector('#sqc-text-colours');
    var maxEl         = root.querySelector('#sqc-text-max');
    var minEl         = root.querySelector('#sqc-text-min');
    var leadingEl     = root.querySelector('#sqc-text-leading');
    var condenseEl    = root.querySelector('#sqc-text-condense');

    var Designer = window.StroopEpsDesigner;
    if (!Designer) {
      console.error('eps-designer.js not loaded — artwork designer unavailable');
      var noop = function () {};
      return { available: false, open: noop, close: noop, redraw: noop, setMode: noop, setText: noop, exportBlocked: noop,
        getText: function () { return null; }, hasText: function () { return false; },
        fontsReady: function () { return false; }, rotationDeg: function () { return 0; }, buildEpsFile: noop,
        buildPreviewFile: function () { return Promise.reject(new Error('The designer is unavailable.')); } };
    }

    var objectUrl = null;
    var swatches  = [];
    var modeError = null;
    var HELP = {
      logo: 'Drag your logo to move it. Drag the square corner handle to resize (hold Shift to resize from the centre), or the round handle to rotate (hold Shift to snap to 15°). On a touchscreen you can also pinch to resize and twist to rotate. The pink line is where the sticker is cut.',
      logoFixed: 'Your logo is centred on the sticker. Drag the square corner handle to resize it, or the round handle to rotate it (hold Shift to snap to 15°). On a touchscreen you can also pinch to resize and twist to rotate. It can’t go past the pink line, which is where the sticker is cut.',
      logoFull: 'Your artwork runs out to the dashed bleed line, past the pink cut line, so nothing is left bare when the sticker is cut. Drag the square corner handle to resize it, or the round handle to rotate it (hold Shift to snap to 15°). Only what is inside the pink line is the finished sticker.',
      text: 'Your text is sized automatically to fit inside the safe margin. The pink line is where the sticker is cut.'
    };
    // Touch screens: no Shift key, so drop those tips and lead with pinch / twist
    if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) {
      ['logo', 'logoFixed', 'logoFull'].forEach(function (k) {
        HELP[k] = HELP[k].replace(/ \(hold Shift[^)]*\)/g, '').replace('On a touchscreen you can also pinch', 'You can also pinch');
      });
    }

    var designer = Designer(canvasEl, {
      widthMm: 75, heightMm: 50, shape: state.shape,
      cornerMm: state.cornerMm, marginMm: state.marginMm, bleedMm: 0,
      borderMm: state.borderMm, borderColor: state.borderColor, padMm: state.padMm, padColor: state.padColor,
      material: state.material,
      fontsUrl: cfg.fontsUrl,
      centerResize: state.centerResize,
      onChange: function (st) { autoFullFill(st); updateStatus(); },
      onMeasure: function (m) { state.cutMm = m; updatePricing(root, state, cfg); },
      onErase: function (u, v) { if (state.onEraseClick) state.onEraseClick(u, v); },
      onColorPicked: function (hex) {
        if (!hex) { setStatus('Click inside your logo to pick a colour.', 'warn'); return; }
        state.bgEnabled = true;
        state.bgColor   = hex;
        bgEnabledEl.checked = true;
        syncBg();
      }
    });

    // ── Full-fill artwork that doesn't reach the edges: stretch its edge pixels, or fill with a logo colour ──
    // Builds a copy of the picture whose outer row/column of pixels is repeated out to the bleed box, then swaps it in.
    function stretchEdges(src, wPx, hPx, targetW, targetH) {
      var k = Math.min(1, 4000 / Math.max(targetW, targetH));   // keep the canvas a sane size
      var EW = Math.max(Math.round(targetW * k), 1), EH = Math.max(Math.round(targetH * k), 1);
      var sw = Math.min(EW, Math.max(1, Math.round(wPx * k))), sh = Math.min(EH, Math.max(1, Math.round(hPx * k)));
      var ox = Math.round((EW - sw) / 2), oy = Math.round((EH - sh) / 2);
      var c = document.createElement('canvas');
      c.width = EW; c.height = EH;
      var g = c.getContext('2d');
      g.imageSmoothingEnabled = false;
      var r = ox + sw, b = oy + sh;
      // edge strips first, then the middle on top
      if (ox > 0) g.drawImage(src, 0, 0, 1, hPx, 0, oy, ox, sh);
      if (EW - r > 0) g.drawImage(src, wPx - 1, 0, 1, hPx, r, oy, EW - r, sh);
      if (oy > 0) g.drawImage(src, 0, 0, wPx, 1, ox, 0, sw, oy);
      if (EH - b > 0) g.drawImage(src, 0, hPx - 1, wPx, 1, ox, b, sw, EH - b);
      // corners: the corner pixel, stretched
      if (ox > 0 && oy > 0) g.drawImage(src, 0, 0, 1, 1, 0, 0, ox, oy);
      if (EW - r > 0 && oy > 0) g.drawImage(src, wPx - 1, 0, 1, 1, r, 0, EW - r, oy);
      if (ox > 0 && EH - b > 0) g.drawImage(src, 0, hPx - 1, 1, 1, 0, b, ox, EH - b);
      if (EW - r > 0 && EH - b > 0) g.drawImage(src, wPx - 1, hPx - 1, 1, 1, r, b, EW - r, EH - b);
      g.imageSmoothingEnabled = true;
      g.imageSmoothingQuality = 'high';
      g.drawImage(src, 0, 0, wPx, hPx, ox, oy, sw, sh);
      return c;
    }

    function applyStretch() {
      var st = designer.getState();
      if (!state.image || !st.fullFill || !st.logo) return;
      if (!state.stretched) state.stretchVector = st.logoVector;
      if (Math.abs(st.rotationDeg || 0) > 0.05) { designer.resetLogo(); st = designer.getState(); }   // stretching assumes the artwork is straight
      var MMpt = Designer.MM, bleed = st.bleedMm;
      var bw = (state.widthMm + 2 * bleed) * MMpt, bh = (state.shape === 'circle' || state.shape === 'square' ? state.widthMm : state.heightMm) + 2 * bleed;
      bh = bh * MMpt;
      var wPx = state.image.naturalWidth || state.image.width, hPx = state.image.naturalHeight || state.image.height;
      var pxPerPt = wPx / st.logo.w;               // the picture's pixels per point at its current size
      var canvas = stretchEdges(state.image, wPx, hPx, Math.max(bw * pxPerPt, wPx), Math.max(bh * pxPerPt, hPx));
      designer.swapImage(canvas, { vector: false });
      designer.fitToBleed();
      state.stretched = true;
      designer.setArtworkNote('edge pixels of the artwork stretched out to the bleed line');
      syncVisibility(); updateStatus();
    }

    function undoStretch() {
      if (!state.stretched || !state.image) return;
      designer.swapImage(state.image, { vector: !!state.stretchVector });
      designer.fitToBleed();
      state.stretched = false;
      designer.setArtworkNote('');
      syncVisibility(); updateStatus();
    }

    function syncGap(st) {
      // the "cover the gap" error from Add Run goes away once the gap is covered
      var errEl = root.querySelector('#sqc-error');
      if (errEl && !errEl.hidden && /full-fill artwork doesn/.test(errEl.textContent) && !gapUnresolved(st)) { errEl.hidden = true; errEl.textContent = ''; }
      var full = !!(st.fullFill && state.image && state.mode === 'logo' && state.shape !== 'diecut');
      var show = full && (state.stretched || gapUnresolved(st));
      gapBox.hidden = !show;
      if (!show) return;
      var gap = !!st.fullFillGap;
      gapText.textContent = state.stretched && !gap
        ? 'Your artwork’s edge pixels are stretched out to the bleed line.'
        : (state.stretched
          ? 'Your artwork no longer reaches every edge (you resized or turned it). Stretch it again, or fill with a colour.'
          : 'Your artwork doesn’t reach every edge of the sticker. Choose how to cover the gap so nothing prints bare:');
      stretchBtn.hidden = state.stretched && !gap;
      stretchBtn.textContent = state.stretched ? 'Stretch again' : 'Stretch the edge pixels';
      gapFillBtn.hidden = state.stretched && !gap;
      unstretchBtn.hidden = !state.stretched;
      gapHint.hidden = state.stretched && !gap;
    }

    stretchBtn.addEventListener('click', applyStretch);
    unstretchBtn.addEventListener('click', undoStretch);
    gapFillBtn.addEventListener('click', function () {
      if (state.stretched) undoStretch();
      state.bgEnabled = true;
      if (!state.bgColor && swatches.length) state.bgColor = swatches[0];
      bgEnabledEl.checked = true;
      bgBox.open = true;
      syncBg();
    });

    function setStatus(msg, kind) {
      statusEl.hidden = !msg;
      statusEl.textContent = msg || '';
      statusEl.className = 'sqc__pane-status' + (kind ? ' sqc__pane-status--' + kind : '');
    }

    // Full-fill artwork that leaves a gap and has nothing covering it (no stretched edges, no fill colour)
    function gapUnresolved(st) { return !!(st.fullFill && st.fullFillGap && !state.bgEnabled); }

    var noWhiteNote = root.querySelector('#sqc-nowhite-note');
    function updateNoWhiteNote(st) {
      var on = materialNoWhite(state.material);
      noWhiteNote.hidden = !on;
      if (!on) return;
      var what = state.material === 'holographic' ? 'holographic foil' : 'clear film';
      var pct = state.mode === 'logo' && state.image ? Math.round((st.whiteFrac || 0) * 100) : 0;
      noWhiteNote.innerHTML = '<strong>White can’t be printed on ' + (state.material === 'holographic' ? 'holographic' : 'clear') + ' stickers.</strong> ' +
        'Our printers don’t have white ink, so anything white in your design is left unprinted and the ' + what + ' shows through instead.' +
        (pct >= 1 ? ' About <strong>' + pct + '%</strong> of your artwork is white, so that part will show the ' + what + ' — check the preview.'
                  : (state.mode === 'logo' && state.image ? ' Your artwork has almost no white, so it should print as you see it.' : ''));
    }
    // Picking Holographic or Clear: drop any white padding / fill colour (it can't be printed)
    state.applyNoWhite = function () {
      if (materialNoWhite(state.material)) {
        if (hexNearWhite(state.padColor)) state.padColor = null;
        if (state.bgColor && hexNearWhite(state.bgColor)) {
          var alt = swatches.filter(function (h) { return !hexNearWhite(h); })[0];
          if (alt) state.bgColor = alt; else { state.bgEnabled = false; bgEnabledEl.checked = false; }
        }
      }
      renderBgSwatches();
      syncBg();
    };

    function updateStatus() {
      var st = designer.getState();
      updateNoWhiteNote(st);
      syncGap(st);
      if (modeError) return setStatus(modeError, 'error');
      if (st.mode === 'text') {
        if (!st.fontsLoaded) return setStatus('Loading text preview…');
        if (st.missingChars.length) return setStatus('No glyph for: ' + st.missingChars.join(' ') + ' — that character will be skipped.', 'warn');
        if (st.overflow) return setStatus('Your text is long — it’s been shrunk below the usual minimum size to fit. Consider shortening it.', 'warn');
        if (state.shape === 'diecut' && st.exportBlocked) return setStatus(st.exportBlocked, 'warn');
        return setStatus('');
      }
      if (st.eyedropper) return setStatus('Click anywhere on your logo in the preview to use that colour.');
      if (state.shape === 'diecut' && state.image && st.exportBlocked) return setStatus(st.exportBlocked, 'warn');
      if (gapUnresolved(st)) return setStatus('Your artwork doesn’t reach every edge of the sticker, so a gap would print bare there. Choose how to cover it in the box under the preview, or untick “My artwork fills the whole sticker”.', 'warn');
      setStatus('');
    }

    function syncVisibility() {
      var logoMode = state.mode === 'logo';
      paneEl.hidden = false; // the size tools live in the preview, so it is always there
      emptyEl.hidden = !(logoMode && !state.image);
      bgBox.hidden  = !logoMode || !state.image || state.shape === 'diecut'; // die-cut uses the padding colour instead
      rmBox.hidden  = !logoMode || !state.image || state.stretched; // Remove background works on the original picture, not a stretched copy
      var st = designer.getState();
      whiteRow.hidden = !(logoMode && state.image && state.shape === 'diecut' && !st.hasAlpha);
      whiteEl.checked = st.whiteBgActive;
      resetBtn.disabled = !state.image;
      helpEl.textContent = state.mode === 'logo' && state.shape !== 'diecut' ? (st.fullFill ? HELP.logoFull : HELP.logoFixed) : HELP[state.mode];
    }

    // ── Background fill ──

    function renderBgSwatches() {
      bgSwatchesEl.innerHTML = '';
      if (!swatches.length) {
        var p = document.createElement('p');
        p.className = 'sqc__hint';
        p.textContent = 'No non-white colours detected in this logo — use the button below to pick one.';
        bgSwatchesEl.appendChild(p);
        return;
      }
      swatches.filter(function (hex) { return !(materialNoWhite(state.material) && hexNearWhite(hex)); }).forEach(function (hex) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'sqc__swatch' + (state.bgColor === hex ? ' sqc__swatch--selected' : '');
        b.style.background = hex;
        b.title = hex;
        b.setAttribute('aria-label', 'Logo colour ' + hex);
        b.addEventListener('click', function () { state.bgColor = hex; syncBg(); });
        bgSwatchesEl.appendChild(b);
      });
    }

    function syncBg() {
      bgControls.hidden = !state.bgEnabled;
      if (state.refreshExtras) state.refreshExtras();
      designer.setBackground(state.bgEnabled ? state.bgColor : null);
      // a fill colour (or full-fill artwork) switches the bleed on; take it off again when nothing prints to the edge
      if (Math.abs(designer.getState().bleedMm - effBleed()) > 0.01) designer.setGeometry({ bleedMm: effBleed() });
      syncFullFill();
      bgCurSwatch.style.background = state.bgColor || 'transparent';
      bgCurHex.textContent = state.bgColor ? state.bgColor.toUpperCase() : 'No colour selected';
      bgSumEl.textContent = state.bgEnabled && state.bgColor ? state.bgColor.toUpperCase() : 'Off';
      var picking = designer.getState().eyedropper;
      eyedropperBtn.classList.toggle('sqc__pane-btn--active', picking);
      eyedropperBtn.setAttribute('aria-pressed', String(picking));
      renderBgSwatches();
      if (typeof original !== 'undefined' && !original && rmHint) rmHint.textContent = rmIntro();
      updateStatus();
    }

    bgEnabledEl.addEventListener('change', function () {
      state.bgEnabled = bgEnabledEl.checked;
      if (state.bgEnabled && !state.bgColor && swatches.length) state.bgColor = swatches[0];
      if (!state.bgEnabled) designer.setEyedropper(false);
      syncBg();
    });

    resetBtn.addEventListener('click', function () { designer.resetLogo(); });

    eyedropperBtn.addEventListener('click', function () {
      stopErasing();
      designer.setEyedropper(!designer.getState().eyedropper);
      syncBg();
    });

    // ── Remove background ──
    // Works on the picture itself; the original is kept so it can be restored.
    var original = null;   // { img, vector } while a removal is applied
    var rmBusy   = false;
    // Intro text depends on whether a fill colour is in play (fill = keep the shape, let the fill show).
    function rmIntro() {
      if (state.shape !== 'diecut' && state.bgEnabled) {
        return 'Makes the plain background around your logo transparent so your fill colour shows through instead of a white box. Works best on a logo sitting on a plain colour.';
      }
      return 'Makes the plain background around your logo transparent. With no fill colour it also switches to Die-cut so the cut line follows your artwork; with Fill background on, your shape stays as it is. Works best on a logo sitting on a plain colour.';
    }
    var RM_MSG = {
      already: 'This image already has a transparent background — nothing to remove.',
      nobg: 'We couldn’t find a plain background around the edge of this image, so nothing was removed. A logo on a plain colour works best.',
      nothing: 'Nothing was close enough to the background colour. Try a higher sensitivity.',
      toomuch: 'That would remove almost the whole image. Try a lower sensitivity.'
    };

    var removalOn = false;   // Remove background has been applied (on top of `original`)
    var erased = [];         // spots the customer erased: [{ u, v }] as fractions of the picture, re-applied after any rebuild
    function stopErasing() { designer.setEraser(false); rmEraseBtn.classList.remove('sqc__pane-btn--active'); rmEraseBtn.setAttribute('aria-pressed', 'false'); }
    function resetRemoval() {
      original = null; removalOn = false; erased = [];
      stopErasing();
      rmInnerEl.checked = false;
      rmEraseUndo.hidden = true;
      rmRestoreBtn.hidden = true;
      rmTune.hidden = true;
      rmBtn.textContent = 'Remove background';
      rmHint.textContent = rmIntro();
    }

    // Apply the erased spots one after another to `img`; resolves with the final picture.
    function applyErased(img, tol) {
      var chain = Promise.resolve(img);
      erased.forEach(function (pt) {
        chain = chain.then(function (cur) {
          return Designer.erasePatch(cur, pt.u, pt.v, tol).then(function (r) { return r.status === 'ok' ? r.image : cur; });
        });
      });
      return chain;
    }
    function currentTol() { return parseInt(rmTol.value, 10) || RMBG_DEFAULT_TOL; }
    // Put `img` on the sticker in place of the current picture (keeps where the customer placed it).
    function showPicture(img) {
      designer.swapImage(img, { vector: false });
      state.image = img;
      swatches = designer.getLogoSwatches();
      if (state.bgColor && swatches.indexOf(state.bgColor) < 0 && swatches.length && !state.bgEnabled) state.bgColor = swatches[0];
      rmRestoreBtn.hidden = false;
      rmEraseUndo.hidden = !erased.length;
      state.whiteBg = null;
      syncVisibility(); syncBg(); updateStatus();
    }

    function applyRemoval() {
      if (!state.image || rmBusy) return;
      rmBusy = true;
      rmBtn.disabled = true;
      var src = original ? original.img : state.image;
      var tol = currentTol();
      Designer.removeBackground(src, tol, rmInnerEl.checked).then(function (r) {
        if (r.status !== 'ok') {
          rmHint.textContent = RM_MSG[r.status] || 'The background could not be removed.';
          if (original && (r.status === 'toomuch' || r.status === 'nothing')) rmTune.hidden = false;
          return;
        }
        if (!original) original = { img: state.image, vector: designer.getState().logoVector };
        removalOn = true;
        return applyErased(r.image, tol).then(function (finalImg) {
          showPicture(finalImg);
          rmBtn.textContent = 'Remove again';
          rmTune.hidden = false;
          var keepShape = state.shape !== 'diecut' && state.bgEnabled && state.bgColor; // fill wanted: keep Rectangle/Square/Circle, the fill shows through
          rmHint.textContent = 'Background removed (' + Math.round(r.removedFrac * 100) + '% of the image)' +
            (keepShape ? ' — your fill colour now shows where it was.' : '.') +
            ' Use Sensitivity if the edge looks rough, tick “clear gaps” or use Erase a spot for patches left inside, or Restore original to undo.';
          if (!keepShape && state.shape !== 'diecut' && state.requestShape) state.requestShape('diecut', true); // no fill: the cut line should follow the artwork
          state.whiteBg = null;
          syncVisibility(); syncBg(); updateStatus();
        });
      }).catch(function (e) {
        console.error(e);
        rmHint.textContent = 'Sorry, the background could not be removed. Please try again or use a PNG that already has a transparent background.';
      }).then(function () {
        rmBusy = false;
        rmBtn.disabled = false;
      });
    }

    // Rebuild the picture from the original after the list of erased spots changed.
    function rebuildErased() {
      if (!original || rmBusy) return;
      rmBusy = true;
      var tol = currentTol();
      var base = Promise.resolve(original.img);
      if (removalOn) base = Designer.removeBackground(original.img, tol, rmInnerEl.checked).then(function (r) { return r.status === 'ok' ? r.image : original.img; });
      base.then(function (img) { return applyErased(img, tol); }).then(function (finalImg) {
        showPicture(finalImg);
      }).catch(function (e) { console.error(e); }).then(function () { rmBusy = false; });
    }

    rmEraseBtn.addEventListener('click', function () {
      var on = !designer.getState().eraser;
      designer.setEraser(on);
      rmEraseBtn.classList.toggle('sqc__pane-btn--active', on);
      rmEraseBtn.setAttribute('aria-pressed', String(on));
      if (on) { designer.setEyedropper(false); rmHint.textContent = 'Click a leftover patch on the preview to make it transparent. Click “Erase a spot” again when you’re done.'; }
      else rmHint.textContent = removalOn ? 'Background removed.' : rmIntro();
      syncBg();
    });
    rmEraseUndo.addEventListener('click', function () {
      if (!erased.length) return;
      erased.pop();
      rebuildErased();
    });
    rmInnerEl.addEventListener('change', function () { applyRemoval(); });
    // a click on the preview while Erase a spot is on
    state.onEraseClick = function (u, v) {
      if (u == null || rmBusy || !state.image) return;
      if (!original) original = { img: state.image, vector: designer.getState().logoVector };
      rmBusy = true;
      var tol = currentTol();
      Designer.erasePatch(state.image, u, v, tol).then(function (r) {
        if (r.status === 'toomuch') { rmHint.textContent = 'That patch is most of your picture — try a lower Sensitivity, or click a smaller spot.'; return; }
        if (r.status !== 'ok') { rmHint.textContent = 'There’s nothing there to erase — click on a coloured patch.'; return; }
        erased.push({ u: u, v: v });
        showPicture(r.image);
        rmTune.hidden = false;
        rmHint.textContent = 'Spot erased. Keep clicking any other leftover patches, then click “Erase a spot” again to finish.';
      }).catch(function (e) { console.error(e); }).then(function () { rmBusy = false; });
    };

    rmBtn.addEventListener('click', applyRemoval);
    rmTol.addEventListener('input', function () { rmVal.textContent = rmTol.value; });
    rmTol.addEventListener('change', function () { if (removalOn) applyRemoval(); else if (erased.length) rebuildErased(); });
    rmRestoreBtn.addEventListener('click', function () {
      if (!original) return;
      designer.swapImage(original.img, { vector: original.vector });
      state.image = original.img;
      swatches = designer.getLogoSwatches();
      resetRemoval();
      state.whiteBg = null;
      syncVisibility();
      syncBg();
      updateStatus();
    });
    whiteEl.addEventListener('change', function () {
      state.whiteBg = whiteEl.checked;
      redraw();
    });

    // ── Text controls ──

    function renderTextColours() {
      var idx = designer.getState().text.colorIndex;
      coloursEl.innerHTML = '';
      Designer.TEXT_COLORS.forEach(function (c, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'sqc__swatch' + (i === idx ? ' sqc__swatch--selected' : '');
        b.style.background = c.hex;
        b.title = c.name;
        b.setAttribute('role', 'radio');
        b.setAttribute('aria-label', c.name);
        b.setAttribute('aria-checked', String(i === idx));
        b.addEventListener('click', function () { designer.setText({ colorIndex: i }); renderTextColours(); });
        coloursEl.appendChild(b);
      });
    }

    function readTextControls() {
      designer.setText({
        text: textEl.value, uppercase: upperEl.checked, bold: boldEl.checked,
        maxSize: maxEl.value, minSize: minEl.value, leading: leadingEl.value, condense: condenseEl.value
      });
      updateStatus();
    }

    [textEl, maxEl, minEl, leadingEl, condenseEl].forEach(function (el) { el.addEventListener('input', readTextControls); });
    [upperEl, boldEl].forEach(function (el) { el.addEventListener('change', readTextControls); });

    // Apply saved text settings (editing a run) to both the designer and the controls.
    function setText(t) {
      designer.setText(t || {});
      var cur = designer.getState().text;
      textEl.value = cur.text;
      upperEl.checked = cur.uppercase;
      boldEl.checked = cur.bold;
      maxEl.value = cur.maxSize;
      minEl.value = cur.minSize;
      leadingEl.value = cur.leading;
      condenseEl.value = cur.condense;
      renderTextColours();
      updateStatus();
    }

    // ── Contract used by attachHandlers ──

    function setMode(mode) {
      state.mode = mode;
      modeError = null;
      designer.setMode(mode, function (err) {
        if (err) { console.error(err); modeError = 'The text preview failed to load. Please refresh the page.'; }
        updateStatus();
      });
      syncVisibility();
      syncBg();
    }

    function openPane(file) {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      objectUrl = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        // SVGs without width/height have no intrinsic size; give them a square box.
        if (!img.naturalWidth || !img.naturalHeight) { img.width = 1000; img.height = 1000; }
        try {
          designer.setImage(img, { vector: file.type === 'image/svg+xml' || /\.svg$/i.test(file.name) });
        } catch (e) {
          // e.g. a browser that blocks reading an SVG's pixels (tainted canvas)
          console.error(e);
          designer.setImage(null);
          img.onerror();
          return;
        }
        state.image = img;
        state.fullFill = false; state.stretched = false; state.fullFillAuto = false; state.fullFillDeclined = false;
        designer.setArtworkNote('');
        resetRemoval();
        state.whiteBg = null;
        swatches = designer.getLogoSwatches();
        state.bgEnabled = false;
        state.bgColor = swatches[0] || null;
        bgEnabledEl.checked = false;
        syncVisibility();
        syncBg();
      };
      img.onerror = function () {
        state.image = null;
        resetBtn.disabled = true;
        setStatus('We couldn’t read that image. Please try a different file.', 'error');
        paneEl.hidden = false;
      };
      img.src = objectUrl;
    }

    function closePane() {
      if (objectUrl) { URL.revokeObjectURL(objectUrl); objectUrl = null; }
      state.image = null;
      state.fullFill = false; state.stretched = false; state.fullFillAuto = false; state.fullFillDeclined = false;
      state.whiteBg = null;
      resetRemoval();
      state.bgEnabled = false;
      state.bgColor = null;
      bgEnabledEl.checked = false;
      swatches = [];
      designer.setEyedropper(false);
      designer.setImage(null);
      syncVisibility();
      syncBg();
    }

    // The bleed the designer actually draws: the customer's setting (never under the floor) once something
    // prints out to the cut line, otherwise none (so a plain sticker's page and file are unchanged).
    // The safe margin is a text-sticker setting. A logo sticker keeps the standard blank frame round the print file, whatever was set for text.
    function effMargin() { return state.mode === 'text' ? state.marginMm : cfg.margin.value; }
    function effBleed() { return bleedApplies(state) ? Math.max(state.bleedMm, cfg.bleed.min) : 0; }
    // Full-fill by itself: opaque artwork the customer drags out to the cut line is a full-fill design, so the bleed (and the
    // run-out to the bleed line) switches on without the tick box. Shrinking it back off the edge switches it off again
    // (only if we switched it on) and leaves it where they put it.
    function autoFullFill(st) {
      if (state.shape === 'diecut' || state.mode !== 'logo' || !state.image) return;
      if (!state.fullFill && st.grew && st.reachesCut && !state.fullFillDeclined) {
        state.fullFill = true; state.fullFillAuto = true;
        if (state.refreshExtras) state.refreshExtras();
        redraw();
      } else if (state.fullFill && state.fullFillAuto && !st.reachesCut && !state.stretched) {
        state.fullFill = false; state.fullFillAuto = false;
        designer.setFullFill(false, true);
        if (state.refreshExtras) state.refreshExtras();
        redraw();
      }
    }
    function syncFullFill() {
      designer.setFullFill(!!state.fullFill && state.shape !== 'diecut' && state.mode === 'logo' && !!state.image);
    }
    // Re-lay-out after any shape/size/corner/margin/bleed change.
    function redraw() {
      // edge-stretched artwork only makes sense while full-fill is on for a rectangle/square/circle logo
      if (state.stretched && (!state.fullFill || state.shape === 'diecut' || state.mode !== 'logo')) undoStretch();
      designer.setMaterial(state.material);
      designer.setDimensionUnit(state.unit);
      // fixed preview scale: the biggest preset of this shape fills the preview, smaller ones draw proportionally smaller
      var refW = 0, refH = 0;
      (window.StroopEpsDesigner.SIZE_PRESETS[state.shape] || []).forEach(function (p) { refW = Math.max(refW, p.w); refH = Math.max(refH, p.h); });
      designer.setViewReference(refW, refH);
      designer.setGeometry({
        widthMm: state.widthMm, heightMm: state.heightMm, shape: state.shape,
        cornerMm: state.cornerMm, marginMm: effMargin(), bleedMm: effBleed(),
        borderMm: state.borderMm, borderColor: state.borderColor,
        padMm: state.padMm, padColor: state.padColor, whiteBg: state.whiteBg
      });
      syncFullFill();
      syncVisibility();
      updateStatus();
      var dm = designer.getState().dimensions;
      canvasEl.setAttribute('aria-label', 'Sticker artwork preview' + (dm ? ', ' + dm.w + (dm.h ? ' by ' + dm.h : '') : ''));
    }

    function buildEpsFile(name) {
      var eps = designer.buildEPS({ title: name.replace(/\.eps$/i, '') });
      return new File([eps], name, { type: 'application/postscript' });
    }

    // Thumbnail for the order emails — see StroopEpsDesigner.buildPreviewPNG.
    function buildPreviewFile(name) {
      return designer.buildPreviewPNG().then(function (blob) {
        return new File([blob], name, { type: 'image/png' });
      });
    }

    renderTextColours();
    syncVisibility();

    return {
      available: true,
      open: openPane,
      close: closePane,
      redraw: redraw,
      setMode: setMode,
      setText: setText,
      getText: function () { return designer.getState().text; },
      hasText: function () { return !!designer.getState().text.text.trim(); },
      fontsReady: function () { return designer.getState().fontsLoaded; },
      // Informational only: the rotation is already baked into the EPS transform.
      rotationDeg: function () { return state.mode === 'logo' ? designer.getState().rotationDeg : 0; },
      exportBlocked: function () { return designer.getState().exportBlocked; },
      gapBlocked: function () { return gapUnresolved(designer.getState()); },
      buildEpsFile: buildEpsFile,
      buildPreviewFile: buildPreviewFile
    };
  }

  // Slider + text box pair. The text box accepts typed values (and the zero
  // label, e.g. "Square") and is clamped to the range when it loses focus.
  function linkSlider(rangeEl, numEl, range, zeroLabel, onChange) {
    var value = range.value;
    rangeEl.min = range.min;
    rangeEl.max = range.max;

    function clamp(v) { return Math.min(range.max, Math.max(range.min, v)); }
    function fmt(v) { return zeroLabel && v === 0 ? zeroLabel : String(v); }
    function parse(raw) {
      var t = String(raw).trim();
      if (zeroLabel && t.toLowerCase() === zeroLabel.toLowerCase()) return 0;
      var v = parseFloat(t);
      return isFinite(v) ? v : null;
    }
    function set(v, keepText) {
      value = clamp(v);
      rangeEl.value = value;
      if (!keepText) numEl.value = fmt(value);
      onChange(value);
    }

    rangeEl.addEventListener('input', function () { set(parseFloat(rangeEl.value)); });
    numEl.addEventListener('input', function () {
      var v = parseFloat(numEl.value);
      if (isFinite(v) && v >= range.min && v <= range.max) set(v, true);
    });
    numEl.addEventListener('change', function () {
      var v = parse(numEl.value);
      set(v === null ? value : v);
    });
    numEl.addEventListener('keydown', function (e) { if (e.key === 'Enter') numEl.blur(); });

    rangeEl.value = value;
    numEl.value = fmt(value);
    return { set: function (v) { set(v); }, reset: function () { set(range.value); } };
  }

  // ─── Wire-up ─────────────────────────────────────────────────────────────────

  function attachHandlers(root, cfg) {
    var maxDiam = Math.min(cfg.maxWidth, cfg.maxHeight);

    var state = {
      mode: 'logo',
      file: null, image: null,
      shape: 'rect', preset: DEFAULT_PRESET,
      unit: 'mm', widthMm: 0, heightMm: 0,
      cornerMm: cfg.corner.value, marginMm: cfg.margin.value, bleedMm: cfg.bleed.value,
      material: 'vinyl',
      borderMm: 0, borderColor: DEFAULT_BORDER_COLOR,
      padMm: cfg.pad.value, padColor: null,
      whiteBg: null,       // die-cut: null = automatic
      requestShape: null, refreshExtras: null,
      cutMm: null,         // die-cut: measured size of the cut outline (mm)
      stretched: false,    // full-fill artwork whose edge pixels have been stretched to the bleed line
      fullFill: false,     // rect/square/circle: the artwork is a full-fill design that runs out to the bleed line
      bgEnabled: false, bgColor: null,
      centerResize: false,
      expedited: false,
      qty: cfg.minQty,
      epsUrl: null,
      previewUrl: null,
      editEps: null,       // { url, previewUrl, key } — an edited logo run's existing artwork
      editingRunIdx: null,
    };

    var pane = setupDesignPane(root, state, cfg);
    // every geometry change also refreshes the row summaries and which controls apply
    var paneRedraw = pane.redraw;
    pane.redraw = function () { syncExtras(); paneRedraw(); };

    function isSingleDim() { return state.shape === 'circle' || state.shape === 'square'; }

    // ── Content mode ──
    var modeGroup   = root.querySelector('#sqc-mode-group');
    var logoSection = root.querySelector('#sqc-logo-section');
    var textSection = root.querySelector('#sqc-text-section');

    function applyMode(mode) {
      modeGroup.querySelectorAll('input[name="sqc-mode"]').forEach(function (r) { r.checked = r.value === mode; });
      syncRadioStyles(modeGroup);
      logoSection.hidden = mode !== 'logo';
      textSection.hidden = mode !== 'text';
      pane.setMode(mode);
      syncExtras();
    }

    modeGroup.addEventListener('change', function (e) {
      if (e.target.name !== 'sqc-mode') return;
      showError(root, null);
      applyMode(e.target.value);
    });

    // ── File ──
    var dropzone    = root.querySelector('#sqc-dropzone');
    var fileInput   = root.querySelector('#sqc-file-input');
    var filePreview = root.querySelector('#sqc-file-preview');
    var fileNameEl  = root.querySelector('#sqc-file-name');
    var fileRemove  = root.querySelector('#sqc-file-remove');
    var resWarning  = root.querySelector('#sqc-res-warning');
    var resDismiss  = root.querySelector('#sqc-res-dismiss');
    var editNote    = root.querySelector('#sqc-edit-note');

    function checkResolution(file) {
      if (!BITMAP_MIME[file.type]) return;
      var reader = new FileReader();
      reader.onload = function (e) {
        var img = new Image();
        img.onload = function () { if (img.naturalWidth * img.naturalHeight < MIN_PIXELS) resWarning.hidden = false; };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    }

    function setFile(file) {
      var err = validateFile(file);
      if (err) { showError(root, err); return; }
      showError(root, null);
      resWarning.hidden = true;
      editNote.hidden   = true;
      state.file = file;
      fileNameEl.textContent = file.name;
      filePreview.hidden = false;
      dropzone.hidden    = true;
      checkResolution(file);
      pane.open(file);
    }

    function clearFile() {
      state.file = null;
      fileInput.value = '';
      filePreview.hidden = true;
      resWarning.hidden  = true;
      dropzone.hidden    = false;
      pane.close();
    }

    resDismiss.addEventListener('click', function () { resWarning.hidden = true; });
    fileInput.addEventListener('change', function () { if (fileInput.files[0]) setFile(fileInput.files[0]); });
    dropzone.addEventListener('dragover',  function (e) { e.preventDefault(); dropzone.classList.add('sqc__dropzone--drag'); });
    dropzone.addEventListener('dragleave', function ()  { dropzone.classList.remove('sqc__dropzone--drag'); });
    dropzone.addEventListener('drop', function (e) {
      e.preventDefault();
      dropzone.classList.remove('sqc__dropzone--drag');
      if (e.dataTransfer.files[0]) setFile(e.dataTransfer.files[0]);
    });
    dropzone.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInput.click(); }
    });
    fileRemove.addEventListener('click', clearFile);

    // ── Shape ──
    var shapeGroup  = root.querySelector('#sqc-shape-group');
    var cornerField = root.querySelector('#sqc-corner-field');
    var rectDims    = root.querySelector('#sqc-rect-dims');
    var circDims    = root.querySelector('#sqc-circ-dims');
    var dimHint     = root.querySelector('#sqc-dim-hint');

    function updateDimHint() {
      var single = isSingleDim();
      var name   = state.shape === 'circle' ? 'diameter' : 'side';
      if (state.unit === 'in') {
        var maxWIn = (cfg.maxWidth  / IN_TO_MM).toFixed(2);
        var maxHIn = (cfg.maxHeight / IN_TO_MM).toFixed(2);
        var maxDIn = (maxDiam       / IN_TO_MM).toFixed(2);
        dimHint.textContent = single
          ? 'Max ' + name + ': ' + maxDIn + ' in'
          : 'Max: ' + maxWIn + ' in \xd7 ' + maxHIn + ' in';
      } else {
        dimHint.textContent = single
          ? 'Max ' + name + ': ' + maxDiam + ' mm'
          : 'Max: ' + cfg.maxWidth + ' mm \xd7 ' + cfg.maxHeight + ' mm';
      }
    }

    // Sync shape radios, dimension inputs and which setting rows apply to state.shape.
    function syncShapeUI() {
      shapeGroup.querySelectorAll('input[name="sqc-shape"]').forEach(function (r) { r.checked = r.value === state.shape; });
      syncRadioStyles(shapeGroup);
      var single = isSingleDim();
      rectDims.hidden = single;
      circDims.hidden = !single;
      var dimName = state.shape === 'circle' ? 'Diameter' : 'Side';
      diamInput.placeholder = dimName;
      diamInput.setAttribute('aria-label', dimName);
      renderPresets();
      updateDimHint();
    }

    // keepSize: switching shape automatically (Remove background -> Die-cut), so keep the chosen size where it is a valid choice
    function chooseShape(shape, keepSize) {
      state.shape = shape;
      syncShapeUI();
      var keep = keepSize && state.widthMm && state.heightMm && matchPreset() !== 'custom';
      if (keep) { state.preset = matchPreset(); syncPresetRadios(); } else applyPreset(DEFAULT_PRESET);
      updatePricing(root, state, cfg);
      pane.redraw();
    }
    state.requestShape = chooseShape;

    shapeGroup.addEventListener('change', function (e) {
      if (e.target.name !== 'sqc-shape') return;
      chooseShape(e.target.value, false);
    });

    // ── Size presets + dimensions ──
    var presetGroup = root.querySelector('#sqc-preset-group');
    var widthInput  = root.querySelector('#sqc-width');
    var heightInput = root.querySelector('#sqc-height');
    var diamInput   = root.querySelector('#sqc-diameter');
    var unitBtns    = root.querySelectorAll('.sqc__unit-btn');

    function presetsFor(shape) {
      return (window.StroopEpsDesigner && window.StroopEpsDesigner.SIZE_PRESETS[shape]) || [];
    }

    function renderPresets() {
      var html = presetsFor(state.shape).map(function (p, i) {
        return radioHTML('sqc-preset', String(i), p.label, state.preset === i);
      });
      html.push(radioHTML('sqc-preset', 'custom', 'Custom size', state.preset === 'custom'));
      presetGroup.innerHTML = html.join('');
    }

    function syncPresetRadios() {
      presetGroup.querySelectorAll('input[name="sqc-preset"]').forEach(function (r) {
        r.checked = r.value === String(state.preset);
      });
      syncRadioStyles(presetGroup);
    }

    function fmtDim(mm) { return state.unit === 'in' ? (mm / IN_TO_MM).toFixed(2) : String(Math.round(mm * 100) / 100); }

    // Write state.widthMm/heightMm into the visible inputs, in the current unit.
    function writeDims() {
      if (isSingleDim()) {
        diamInput.value = state.widthMm ? fmtDim(state.widthMm) : '';
      } else {
        widthInput.value  = state.widthMm  ? fmtDim(state.widthMm)  : '';
        heightInput.value = state.heightMm ? fmtDim(state.heightMm) : '';
      }
    }

    function applyPreset(i) {
      var p = presetsFor(state.shape)[i];
      if (!p) { state.preset = 'custom'; syncPresetRadios(); return; }
      state.preset   = i;
      state.widthMm  = p.w;
      state.heightMm = p.h;
      writeDims();
      syncPresetRadios();
    }

    function matchPreset() {
      var list = presetsFor(state.shape);
      for (var i = 0; i < list.length; i++) {
        if (list[i].w === state.widthMm && list[i].h === state.heightMm) return i;
      }
      return 'custom';
    }

    presetGroup.addEventListener('change', function (e) {
      if (e.target.name !== 'sqc-preset') return;
      if (e.target.value === 'custom') { state.preset = 'custom'; syncPresetRadios(); }
      else applyPreset(parseInt(e.target.value, 10));
      updatePricing(root, state, cfg);
      pane.redraw();
    });

    function toMm(v) { return state.unit === 'in' ? v * IN_TO_MM : v; }
    function parseDim(input) {
      var v = parseFloat(input.value);
      return (isNaN(v) || v <= 0) ? 0 : toMm(v);
    }

    function onManualDim() {
      state.preset = 'custom';
      syncPresetRadios();
      updatePricing(root, state, cfg);
      pane.redraw();
    }

    widthInput.addEventListener('input', function () { state.widthMm = parseDim(widthInput); onManualDim(); });
    heightInput.addEventListener('input', function () { state.heightMm = parseDim(heightInput); onManualDim(); });
    diamInput.addEventListener('input', function () {
      var d = parseDim(diamInput);
      state.widthMm = d; state.heightMm = d;
      onManualDim();
    });

    unitBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var newUnit = btn.dataset.unit;
        if (newUnit === state.unit) return;
        state.unit = newUnit;
        writeDims(); // state stays in mm; only the display changes
        updateDimHint();
        unitBtns.forEach(function (b) {
          b.classList.toggle('sqc__unit-btn--active', b.dataset.unit === newUnit);
          b.setAttribute('aria-pressed', String(b.dataset.unit === newUnit));
        });
        updatePricing(root, state, cfg);
        pane.redraw(); // the dimension lines in the preview follow the unit
      });
    });

    // ── Corner / margin / bleed ──
    function geometrySlider(id, key, range, zeroLabel) {
      return linkSlider(root.querySelector('#' + id), root.querySelector('#' + id + '-num'), range, zeroLabel, function (v) {
        if (state[key] === v) return;
        state[key] = v;
        pane.redraw();
      });
    }
    var cornerSlider = geometrySlider('sqc-corner', 'cornerMm', cfg.corner, 'Square');
    var marginSlider = geometrySlider('sqc-margin', 'marginMm', cfg.margin, null);
    var bleedSlider  = geometrySlider('sqc-bleed',  'bleedMm',  cfg.bleed,  null);
    var padSlider    = geometrySlider('sqc-pad',    'padMm',    cfg.pad,    'None');

    // ── Material ──
    var materialGroup = root.querySelector('#sqc-material-group');
    var materialNote  = root.querySelector('#sqc-material-note');
    function syncMaterialUI() {
      materialGroup.querySelectorAll('input[name="sqc-material"]').forEach(function (r) { r.checked = r.value === state.material; });
      syncRadioStyles(materialGroup);
      var pct = cfg.materialPct[state.material] || 0;
      materialNote.textContent = MATERIAL_NOTES[state.material] + ' The preview finish is approximate.' +
        (pct ? ' Price ' + (pct > 0 ? '+' : '') + pct + '%.' : '');
    }
    materialGroup.addEventListener('change', function (e) {
      if (e.target.name !== 'sqc-material') return;
      state.material = MATERIAL_NAMES[e.target.value] ? e.target.value : 'vinyl';
      syncMaterialUI();
      if (state.applyNoWhite) state.applyNoWhite();
      updatePricing(root, state, cfg);
      pane.redraw();
    });

    // ── Border and padding colours ──
    var padColoursEl    = root.querySelector('#sqc-pad-colours');
    function colourOptions(withNone) {
      var list = Designer_colours().map(function (c) { return { name: c.name, hex: c.hex }; });
      if (!materialNoWhite(state.material)) list.push({ name: 'White', hex: '#ffffff' });
      if (withNone) list.unshift({ name: 'None', hex: null });
      return list;
    }
    function Designer_colours() { return (window.StroopEpsDesigner && window.StroopEpsDesigner.TEXT_COLORS) || []; }
    function renderColourSwatches(el, withNone, current, onPick) {
      el.innerHTML = '';
      colourOptions(withNone).forEach(function (c) {
        var on = (c.hex || null) === (current || null);
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'sqc__swatch' + (c.hex ? '' : ' sqc__swatch--none') + (on ? ' sqc__swatch--selected' : '');
        if (c.hex) b.style.background = c.hex;
        b.title = c.name;
        b.setAttribute('role', 'radio');
        b.setAttribute('aria-label', c.name);
        b.setAttribute('aria-checked', String(on));
        b.addEventListener('click', function () { onPick(c.hex); });
        el.appendChild(b);
      });
    }
    function renderBorderPadColours() {
      renderColourSwatches(padColoursEl, true, state.padColor, function (hex) { state.padColor = hex; pane.redraw(); });
    }

    // ── Which rows apply, their summaries, and when Bleed is useful ──
    var cornerSum = root.querySelector('#sqc-corner-sum');
    var padSum    = root.querySelector('#sqc-pad-sum');
    var padField  = root.querySelector('#sqc-pad-field');
    var padBleedNote = root.querySelector('#sqc-pad-bleed-note');
    var sizeSum   = root.querySelector('#sqc-size-sum');
    var marginSum = root.querySelector('#sqc-margin-sum');
    var marginField = root.querySelector('#sqc-margin-field');
    var bleedSum  = root.querySelector('#sqc-bleed-sum');
    var bleedField = root.querySelector('#sqc-bleed-field');
    var fullFillRow  = root.querySelector('#sqc-fullfill-row');
    var fullFillHint = root.querySelector('#sqc-fullfill-hint');
    var fullFillEl   = root.querySelector('#sqc-fullfill');
    var bleedBody    = root.querySelector('#sqc-bleed-body');
    var bleedNone    = root.querySelector('#sqc-bleed-none');
    var diecutNote = root.querySelector('#sqc-diecut-note');
    var lastColourKey = null;
    function mmText(v, zero) { return v === 0 && zero ? zero : (Math.round(v * 100) / 100) + ' mm'; }
    function syncExtras() {
      var diecut = state.shape === 'diecut';
      cornerField.hidden = state.shape === 'circle' || diecut;        // circles are always round; die-cut follows the artwork
      padField.hidden = !diecut;
      marginField.hidden = state.mode !== 'text';
      diecutNote.hidden = !diecut;
      cornerSum.textContent = mmText(state.cornerMm, 'Square');
      marginSum.textContent = mmText(state.marginMm, 'None');
      if (!state.widthMm) sizeSum.textContent = '';
      else if (state.shape === 'circle') sizeSum.textContent = '\u00d8 ' + fmtDim(state.widthMm) + ' ' + state.unit;
      else if (state.shape === 'square') sizeSum.textContent = fmtDim(state.widthMm) + ' ' + state.unit + ' square';
      else sizeSum.textContent = (diecut ? 'up to ' : '') + fmtDim(state.widthMm) + ' \u00d7 ' + fmtDim(state.heightMm) + ' ' + state.unit;
      var edgeBleed = diecut && state.padMm === 0 && !state.padColor && (state.mode === 'text' || !!state.image);
      padSum.textContent = mmText(state.padMm, 'None') + (edgeBleed ? ' · edge bleed added' : (state.padColor ? '' : ' · not printed'));
      padBleedNote.hidden = !edgeBleed;
      // bleed matters once something is printed out to the cut line; the full-fill tick box lives in the same row
      fullFillEl.checked = !!state.fullFill;
      var logoWithImage = state.mode === 'logo' && !!state.image;
      var canFullFill = !diecut && logoWithImage;
      var applies = bleedApplies(state);
      bleedField.hidden = !(applies || canFullFill);
      fullFillRow.hidden = !canFullFill;
      fullFillHint.hidden = !(canFullFill && state.fullFill);
      fullFillHint.textContent = (state.fullFillAuto ? 'Your design reaches the edge, so we’ve switched on full-fill and bleed for you. ' : '') +
        'Your artwork is scaled up to the dashed bleed line so nothing is left bare at the cut line. Drag the corner handle to resize it' +
        (state.fullFillAuto ? ' (make it smaller to turn this off).' : '.');
      bleedBody.hidden = !applies;
      bleedNone.hidden = applies;
      bleedSum.textContent = applies ? mmText(Math.max(state.bleedMm, cfg.bleed.min)) + (state.fullFill && canFullFill ? ' · full-fill' : '') : 'Not needed';
      var key = state.padColor + '|' + state.material;
      if (key !== lastColourKey) { lastColourKey = key; renderBorderPadColours(); }
    }
    state.refreshExtras = syncExtras;
    fullFillEl.addEventListener('change', function () {
      state.fullFill = fullFillEl.checked;
      state.fullFillAuto = false;
      state.fullFillDeclined = !fullFillEl.checked;   // they took it off themselves: don't switch it on again behind their back
      pane.redraw();   // pushes the bleed first, then the full-fill sizing
    });

    // ── Expedited ──
    var expeditedCheck = root.querySelector('#sqc-expedited');
    expeditedCheck.addEventListener('change', function () {
      state.expedited = expeditedCheck.checked;
      updatePricing(root, state, cfg);
    });

    // ── Quantity ──
    var qtyInput = root.querySelector('#sqc-qty');
    qtyInput.addEventListener('input', function () {
      state.qty = parseInt(qtyInput.value, 10) || 0;
      updatePricing(root, state, cfg);
    });

    // ─── Order Manager helpers ────────────────────────────────────────────────

    // Everything baked into the EPS besides the artwork itself. An edited logo
    // run can keep its uploaded EPS only while this is unchanged.
    function geometryKey() {
      return [state.shape, Math.round(state.widthMm), Math.round(state.heightMm),
        state.cornerMm, state.marginMm, state.bleedMm, state.fullFill ? 1 : 0,
        state.material, state.borderMm, state.borderColor, state.padMm, state.padColor].join('|');
    }

    function reusableEps() {
      return state.mode === 'logo' && !state.image && state.editEps && state.editEps.url &&
        !state.editEps.expired && state.editEps.key === geometryKey() ? state.editEps.url : null;
    }

    // When the EPS is reused the design is byte-identical, so its preview is too.
    function reusablePreview() {
      return state.editEps ? (state.editEps.previewUrl || null) : null;
    }

    function loadRunIntoCalculator(run, idx) {
      clearFile();
      state.shape     = SHAPE_NAMES[run.shape] ? run.shape : 'rect';
      state.widthMm   = run.workWidthMm || run.widthMm;
      state.heightMm  = run.workHeightMm || run.heightMm;
      // die-cut re-using its stored artwork has no outline to measure, so keep the size it was priced on
      state.cutMm     = run.shape === 'diecut' ? { w: run.widthMm, h: run.heightMm } : null;
      state.preset    = matchPreset();
      state.qty       = run.qty;
      state.expedited = !!run.expedited;
      state.editingRunIdx = idx;

      syncShapeUI();
      writeDims();
      // Runs saved before the designer have no geometry settings — use the defaults.
      cornerSlider.set(run.cornerMm != null ? run.cornerMm : cfg.corner.value);
      marginSlider.set(run.marginMm != null ? run.marginMm : cfg.margin.value);
      bleedSlider.set(run.bleedMm ? run.bleedMm : cfg.bleed.value);
      state.fullFill = !!run.fullFill;
      state.material    = MATERIAL_NAMES[run.material] ? run.material : 'vinyl';
      state.borderColor = run.borderColor || DEFAULT_BORDER_COLOR;
      state.padColor    = run.padColor || null;
      state.whiteBg     = null;
      state.borderMm = 0;   // the border tool has been removed; older saved runs load without it
      padSlider.set(run.padMm != null ? run.padMm : cfg.pad.value);
      syncMaterialUI();
      lastColourKey = null;

      applyMode(run.mode === 'text' ? 'text' : 'logo');
      if (state.mode === 'text') pane.setText(run.text);

      // Pre-designer runs stored fileUrl / croppedFileUrl instead of epsUrl.
      var url = run.epsUrl || run.croppedFileUrl || run.fileUrl || null;
      // An expired run keeps its stored URL for reference but can't reuse it:
      // the file may already be gone, so the logo has to be uploaded again.
      state.editEps = state.mode === 'logo' && url
        ? { url: url, previewUrl: run.previewUrl || null, key: geometryKey(), expired: runExpired(run) }
        : null;
      editNote.hidden = !state.editEps;
      if (state.editEps && state.editEps.expired) {
        editNote.textContent = 'This run\u2019s artwork has expired. Please upload the logo again.';
      }

      qtyInput.value         = run.qty;
      expeditedCheck.checked = !!run.expedited;

      updatePricing(root, state, cfg);
      pane.redraw();
      root.scrollIntoView({ behavior: 'smooth' });
    }

    function renderRuns(runs, omRoot) {
      var list    = omRoot.querySelector('#sqc-runs-list');
      var actions = omRoot.querySelector('#sqc-order-actions');
      if (!list) return;

      if (!runs.length) {
        list.innerHTML = '<p style="color:#666;">No runs added yet.</p>';
        if (actions) actions.style.display = 'none';
        return;
      }

      if (actions) actions.style.display = '';

      list.innerHTML = runs.map(function (run, i) {
        var expired = runExpired(run);
        return [
          '<div style="border:1px solid ' + (expired ? 'var(--color-accent,#e44)' : '#ddd') + ';padding:0.75rem;margin-bottom:0.5rem;border-radius:4px;">',
            '<strong>Run ' + (i + 1) + '</strong>: ',
            (SHAPE_NAMES[run.shape] || SHAPE_NAMES.rect) + (run.mode === 'text' ? ' text' : '') + ', ',
            (MATERIAL_NAMES[run.material] && run.material !== 'vinyl' ? MATERIAL_NAMES[run.material] + ', ' : ''),
            run.widthMm + ' &times; ' + run.heightMm + ' mm, ',
            'Qty: ' + run.qty + ', ',
            fmtCurrency(run.quote.totalPrice, cfg.currency),
            run.expedited ? ' <em>(Express)</em>' : '',
            expired
              ? '<br><strong style="color:var(--color-accent,#e44)">Artwork expired &mdash; please re-upload</strong>'
              : '',
            '<br>',
            '<button type="button" data-run-edit="' + i + '" style="margin-top:0.5rem;">Edit</button>',
            '<button type="button" data-run-remove="' + i + '" style="margin-left:0.5rem;">Remove</button>',
          '</div>',
        ].join('');
      }).join('');

      list.querySelectorAll('[data-run-edit]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var idx = parseInt(btn.getAttribute('data-run-edit'), 10);
          loadRunIntoCalculator(runs[idx], idx);
        });
      });

      list.querySelectorAll('[data-run-remove]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var idx     = parseInt(btn.getAttribute('data-run-remove'), 10);
          var updated = runs.slice();
          updated.splice(idx, 1);
          saveRuns(updated);
          renderRuns(updated, omRoot);
        });
      });
    }

    function checkDraftOrderResume(omRoot) {
      var draft     = loadDraftOrder();
      var resumeEl  = omRoot.querySelector('#sqc-draft-resume');
      if (!resumeEl) return;

      // Past its expiry the draft has been deleted by the worker's sweep and
      // its invoice URL 404s, so drop it rather than offering a dead link.
      if (draft && draft.expiresAt && Date.parse(draft.expiresAt) <= Date.now()) {
        clearDraftOrder();
        resumeEl.style.display = 'none';
        return;
      }

      if (draft && draft.invoiceUrl) {
        resumeEl.style.display = '';
        var resumeBtn  = omRoot.querySelector('#sqc-resume-btn');
        var discardBtn = omRoot.querySelector('#sqc-discard-btn');

        if (resumeBtn) {
          resumeBtn.addEventListener('click', function () {
            window.location.href = draft.invoiceUrl;
          });
        }
        if (discardBtn) {
          discardBtn.addEventListener('click', function () {
            clearDraftOrder();
            resumeEl.style.display = 'none';
            renderRuns(loadRuns(), omRoot);
          });
        }
      } else {
        resumeEl.style.display = 'none';
      }
    }

    // ── Submit ──
    var addRunBtn = root.querySelector('#sqc-add-run');
    var inquiryEl = root.querySelector('#sqc-inquiry');

    function snapshotCurrentItem() {
      var quote = quoteFor(state, cfg);
      var pd = pricedDims(state);
      return {
        mode:      state.mode,
        material:  state.material,
        borderMm:  state.borderMm,
        borderColor: state.borderColor,
        padMm:     state.padMm,
        padColor:  state.padColor,
        epsUrl:    state.epsUrl,
        previewUrl: state.previewUrl,
        shape:     state.shape,
        // die-cut: the real (priced) outline size; the chosen limit is kept for editing the run
        widthMm:   Math.round(state.shape === 'diecut' ? pd.w : state.widthMm),
        heightMm:  Math.round(state.shape === 'diecut' ? pd.h : state.heightMm),
        workWidthMm:  state.shape === 'diecut' ? Math.round(state.widthMm) : undefined,
        workHeightMm: state.shape === 'diecut' ? Math.round(state.heightMm) : undefined,
        cornerMm:  state.cornerMm,
        marginMm:  state.mode === 'text' ? state.marginMm : cfg.margin.value,   // safe margin is a text-sticker setting
        bleedMm:   bleedApplies(state) ? Math.max(state.bleedMm, cfg.bleed.min) : 0,
        fullFill:  !!(state.fullFill && state.shape !== 'diecut' && state.mode === 'logo'),
        edgeStretch: !!(state.stretched && state.fullFill && state.shape !== 'diecut' && state.mode === 'logo'),
        text:      state.mode === 'text' ? pane.getText() : null,
        qty:       state.qty,
        expedited: state.expedited,
        rotation:  pane.rotationDeg(),
        quote:     quote,
        // When this run's artwork reached R2 — the clock for runExpired().
        uploadedAt: new Date().toISOString(),
      };
    }

    function epsFileName() {
      var base = state.mode === 'text'
        ? (pane.getText().text.split('\n')[0] || '')
        : (state.file ? state.file.name.replace(/\.[a-z0-9]+$/i, '') : '');
      base = base.replace(/[^a-z0-9\-_. ]/gi, '').trim().replace(/\s+/g, '_').slice(0, 40);
      // the worker doesn't know about die-cut or materials, so the file name tells production
      var tags = (state.shape === 'diecut' ? '_diecut' : '') + (state.material !== 'vinyl' ? '_' + state.material : '');
      return (base || 'sticker') + tags + '.eps';
    }

    // Build the print-ready EPS from the designer and upload it — the run's one file.
    function uploadEps() {
      var file;
      try {
        file = pane.buildEpsFile(epsFileName());
      } catch (e) {
        console.error(e);
        return Promise.reject(new Error('We couldn’t build your artwork. Please try again.'));
      }
      if (file.size > MAX_BYTES) return Promise.reject(new Error('The artwork file is too large. Please try a smaller logo image.'));
      addRunBtn.textContent = 'Uploading artwork…';
      return uploadFile(file, cfg.workerUrl);
    }

    function previewFileName() {
      return epsFileName().replace(/\.eps$/i, '') + '-preview.png';
    }

    // The order emails show this thumbnail. It is a nicety, not part of the
    // order: a failure here resolves null so Add Run still completes and the
    // customer keeps their run. runArtworkUrl() in the worker never picks it,
    // so a missing preview can't affect the fulfilment email either.
    function uploadPreview() {
      addRunBtn.textContent = 'Building preview…';
      return pane.buildPreviewFile(previewFileName())
        .then(function (file) { return uploadFile(file, cfg.workerUrl); })
        .catch(function (e) {
          console.error('Preview image skipped:', (e && e.message) || e);
          return null;
        });
    }

    function resetForm() {
      clearFile();
      editNote.hidden = true;
      state.editEps = null;
      state.epsUrl  = null;
      state.previewUrl = null;
      if (state.mode === 'text') pane.setText({ text: '' });
      state.shape = 'rect';
      syncShapeUI();
      applyPreset(DEFAULT_PRESET);
      cornerSlider.reset();
      marginSlider.reset();
      bleedSlider.reset();
      padSlider.reset();
      state.material = 'vinyl';
      state.borderColor = DEFAULT_BORDER_COLOR;
      state.padColor = null;
      state.whiteBg = null;
      syncMaterialUI();
      lastColourKey = null;
      qtyInput.value     = cfg.minQty;
      state.qty          = cfg.minQty;
      expeditedCheck.checked = false;
      state.expedited    = false;
      updatePricing(root, state, cfg);
      pane.redraw();
      showError(root, null);
    }

    // ─── Add Run button ───────────────────────────────────────────────────────
    if (addRunBtn) {
      addRunBtn.addEventListener('click', function () {
        showError(root, null);
        var reuseUrl = reusableEps();
        var err = validateState(state, cfg, pane, reuseUrl);
        if (err) { showError(root, err); return; }

        addRunBtn.disabled = true;
        addRunBtn.textContent = reuseUrl ? 'Adding run…' : 'Preparing artwork…';

        // Let the button repaint before the (synchronous) EPS build.
        new Promise(function (resolve) { setTimeout(resolve, 0); })
          .then(function () { return reuseUrl || uploadEps(); })
          .then(function (url) {
            state.epsUrl = url;
            return reuseUrl ? reusablePreview() : uploadPreview();
          })
          .then(function (previewUrl) {
            state.previewUrl = previewUrl || null;
            var item = snapshotCurrentItem();
            var runs = loadRuns();
            if (typeof state.editingRunIdx === 'number' && state.editingRunIdx >= 0) {
              runs[state.editingRunIdx] = item;
              state.editingRunIdx = null;
            } else {
              runs.push(item);
            }
            saveRuns(runs);
            renderRuns(runs, root);
            resetForm();
            addRunBtn.disabled = false;
            addRunBtn.textContent = 'Add Run';
          }).catch(function (e) {
            showError(root, e.message || 'Something went wrong.');
            addRunBtn.disabled = false;
            addRunBtn.textContent = 'Add Run';
          });
      });
    }

    // ─── Checkout All button ──────────────────────────────────────────────────
    var checkoutAllBtn  = root.querySelector('#sqc-checkout-all');
    var guestEmailRow   = root.querySelector('#sqc-guest-email-row');
    var guestEmailInput = root.querySelector('#sqc-guest-email');
    var approvalCheck   = root.querySelector('#sqc-artwork-approval');

    if (!cfg.customerEmail && guestEmailRow) guestEmailRow.style.display = '';

    // The retention period is the worker's to state, so the copy only appears
    // once GET /config has answered. Re-render the runs then too: expiry can't
    // be judged until the period is known.
    var retentionNote = root.querySelector('#sqc-retention-note');
    fetchRetention(cfg.workerUrl).then(function (days) {
      if (!days || !retentionNote) return;
      retentionNote.textContent = 'Your quote is held for ' + days + (days === 1 ? ' day' : ' days') + ' after checkout.';
      retentionNote.hidden = false;
      renderRuns(loadRuns(), root);
    });

    if (checkoutAllBtn) {
      checkoutAllBtn.addEventListener('click', function () {
        var runs = loadRuns();
        if (!runs.length) { showError(root, 'No runs to check out.'); return; }

        // Expired runs point at artwork that may no longer exist, so the order
        // would be unproduceable. Edit the run and re-upload to clear it.
        if (runs.some(runExpired)) {
          showError(root, 'Some runs have expired artwork. Please edit those runs and re-upload before checking out.');
          return;
        }

        var email = cfg.customerEmail;
        if (!email) {
          email = guestEmailInput ? guestEmailInput.value.trim() : '';
          if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            showError(root, 'Please enter a valid email address to continue.');
            if (guestEmailInput) guestEmailInput.focus();
            return;
          }
        }

        if (approvalCheck && !approvalCheck.checked) {
          showError(root, 'Please confirm Artwork Approval to continue.');
          approvalCheck.focus();
          return;
        }

        checkoutAllBtn.disabled = true;
        checkoutAllBtn.textContent = 'Creating order…';

        createOrder(runs, email, cfg.customerId, cfg)
          .then(function (draft) {
            saveDraftOrder({ invoiceUrl: draft.invoiceUrl, runs: runs, expiresAt: draft.expiresAt || null });
            clearAllRuns();
            renderRuns([], root);
            if (approvalCheck) approvalCheck.checked = false;
            window.location.href = draft.invoiceUrl;
          })
          .catch(function (e) {
            showError(root, e.message || 'Something went wrong. Please try again.');
            checkoutAllBtn.disabled = false;
            checkoutAllBtn.textContent = 'Proceed to Checkout';
          });
      });
    }

    // ─── Clear All button ─────────────────────────────────────────────────────
    var clearOrderBtn = root.querySelector('#sqc-clear-order');
    if (clearOrderBtn) {
      clearOrderBtn.addEventListener('click', function () {
        if (!confirm('Clear all runs?')) return;
        clearAllRuns();
        renderRuns([], root);
      });
    }

    // ── Inquiry form (fallback when no worker) ──
    var inqSendBtn = root.querySelector('#sqc-inq-send');
    var inqNameEl  = root.querySelector('#sqc-inq-name');
    var inqEmailEl = root.querySelector('#sqc-inq-email');
    var inqErrorEl = root.querySelector('#sqc-inq-error');

    function showInqError(msg) { inqErrorEl.hidden = !msg; if (msg) inqErrorEl.textContent = msg; }

    inqSendBtn.addEventListener('click', function () {
      showInqError(null);
      var name  = inqNameEl.value.trim();
      var email = inqEmailEl.value.trim();
      if (!name)  { showInqError('Please enter your name.'); inqNameEl.focus(); return; }
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        showInqError('Please enter a valid email address.'); inqEmailEl.focus(); return;
      }
      var quote   = quoteFor(state, cfg);
      var pdq     = pricedDims(state);
      var dimDesc = state.shape === 'circle'
        ? 'Diameter: ' + Math.round(state.widthMm) + ' mm'
        : 'Dimensions: ' + Math.round(pdq.w) + ' x ' + Math.round(pdq.h) + ' mm';
      var artworkDesc = state.mode === 'text'
        ? (pane.hasText() ? 'Text: ' + pane.getText().text.slice(0, 200) : '')
        : (state.file ? 'Artwork: ' + state.file.name + ' (to be uploaded separately)' : '');
      var body = [
        'Name: ' + name, 'Email: ' + email, '',
        '--- Quote Details ---',
        'Shape: ' + SHAPE_NAMES[state.shape],
        'Material: ' + MATERIAL_NAMES[state.material],
        dimDesc,
        'Quantity: ' + state.qty,
        'Expedited: ' + (state.expedited ? 'Yes' : 'No'),
        'Unit Price: ' + fmtCurrency(quote.unitPrice, cfg.currency),
        'Total Price: ' + fmtCurrency(quote.totalPrice, cfg.currency),
        'Est. Delivery: ' + quote.days + '–' + (quote.days + 2) + ' business days',
        artworkDesc,
      ].filter(Boolean).join('\n');

      inqSendBtn.disabled = true;
      inqSendBtn.textContent = 'Sending…';

      var form = document.createElement('form');
      form.method = 'POST';
      form.action = '/contact#contact_form';
      var fields = { 'form_type': 'contact', 'utf8': '✓', 'contact[name]': name, 'contact[email]': email, 'contact[body]': body };
      Object.keys(fields).forEach(function (k) {
        var inp = document.createElement('input');
        inp.type = 'hidden'; inp.name = k; inp.value = fields[k];
        form.appendChild(inp);
      });
      document.body.appendChild(form);
      form.submit();
    });

    // ─── Initial form state ───────────────────────────────────────────────────
    syncShapeUI();
    applyPreset(DEFAULT_PRESET);
    syncMaterialUI();
    updatePricing(root, state, cfg);
    pane.redraw();

    // ─── Initialise order manager ─────────────────────────────────────────────
    var omRoot = root.querySelector('#sqc-order-manager');
    if (omRoot) {
      renderRuns(loadRuns(), omRoot);
      checkDraftOrderResume(omRoot);
    }
  }

  // ─── Init ─────────────────────────────────────────────────────────────────────

  function init() {
    var mount = document.getElementById('sticker-quote-app');
    if (!mount) return;
    if (!document.getElementById('sqc-styles')) {
      var style = document.createElement('style');
      style.id = 'sqc-styles';
      style.textContent = CSS;
      document.head.appendChild(style);
    }
    var cfg     = readConfig(mount);
    var wrapper = document.createElement('div');
    wrapper.className = 'sqc';
    wrapper.innerHTML = buildHTML(cfg);
    mount.innerHTML   = '';
    mount.appendChild(wrapper);
    attachHandlers(wrapper, cfg);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();